import { AstraFiCard } from '../../../src/types/astraFi';
import {
  BankAccountStatus,
  CustomerKycStatus,
  CustomerTosStatus,
  PaymentLinkStatus,
  PaymentStatus,
  PaymentType,
  PayoutStatus,
  PayoutType,
  PriceType,
  RequestType,
  ROLES,
  SphereEventNames,
  SubscriptionStatus,
} from '../../../src/enums/enums';
import { LocaleResource } from './en';
import {
  AMOUNT_OFF_RAMPED,
  AMOUNT_ON_RAMPED,
  AVERAGE_SPEND_PER_CUSTOMER,
  FAILED_PAYMENTS,
  MRR,
  NET_VOLUME,
  SUCCESSFUL_PAYMENTS,
} from '../quill';

export const es = {
  main: {
    navbar: {
      home: 'Inicio',
      customers: 'Clientes',
      about: 'Acerca de',
      developers: 'Desarrolladores',
      language: 'Idioma',
    },
    hero: {
      topBadge: 'Soluciones globales de pagos',
      header: 'Envía dinero a todo el mundo',
      subHeading:
        'Desde dólares y euros hasta pesos y más, Sphere lleva las conversiones de divisas al futuro.',
      ctas: {
        primary: 'Para empresas',
        secondary: 'Para individuos',
      },
    },
    solutions: {
      header: {
        topBadge: 'Procesamiento de pagos optimizado',
        header: 'Pagos internacionales para empresas',
        subHeading:
          'Mejora el movimiento de dinero—desde transferencias bancarias y tarjetas hasta el soporte para todas las redes principales—\ntransferir dinero a través de internet nunca ha sido tan fácil.',
        ctas: {
          primary: 'Empieza ahora',
          secondary: 'Descubre más',
        },
        cards: {
          '0': {
            title: 'Para remesas internacionales',
            description:
              'Envía tu moneda preferida, y tus proveedores reciben USD, MXN, BRL, EUR a tasas de cambio inigualables.',
            bullets: {
              '0': 'Cumplimiento y seguridad',
              '1': 'Liquidación en tiempo real',
              '2': 'Cobertura global',
            },
          },
          '1': {
            title: 'Para individuos de alto patrimonio',
            description:
              'Servicios OTC, conversiones de divisas sin esfuerzo y soluciones completas para la gestión y seguimiento financiero.',
            bullets: {
              '0': 'Soporte dedicado 24/7',
              '1': 'Alta liquidez',
              '2': 'Privacidad y encriptación',
            },
          },
          '2': {
            title: 'Para Finanzas y FinTechs',
            description:
              'Acepta pagos como prefieras, aprovecha las conversiones automáticas en tu cuenta, y ofrece servicios de conversión personalizados para tus clientes.',
            bullets: {
              '0': 'Acuerdos de nivel de servicio',
              '1': 'Protección al cliente',
              '2': 'API y Soluciones que no se requiere código',
            },
          },
        },
      },
    },
    ramp: {
      topBadge: 'Soluciones de conversión',
      header: 'Conectando monedas globales',
      subHeading:
        'Sphere se encarga de las complejidades de ingeniería, operativas y regulatorias por ti—para que no tengas que hacerlo.',
      solutions: {
        '0': {
          title: 'Personalizado con tu marca',
          description:
            'Sphere maneja las complejidades de ingeniería, operativas y regulatorias—para que no tengas que hacerlo.',
          bullets: {
            '0': 'Disponible vía API, no-code, y widget embebible.',
            '1': 'Ahorra innumerables horas en el proceso de desarrollo.',
            '2': 'Precios inigualables',
          },
        },
        '1': {
          title: 'Conversión de monedas, fácil y rápido.',
          description:
            'Genera una billetera vinculada a tu cuenta bancaria. Envía monedas y recibe USD con un solo clic. Disponible para comerciantes seleccionados a través del panel de control de Sphere.',
        },
        '2': {
          title: 'Cumplimiento unificado',
          description:
            'Confía en nuestra experiencia global y licencias regulatorias para brindar la mejor experiencia de pagos a tus usuarios. Deja que nos encarguemos del monitoreo de transacciones, la verificación de sanciones y la detección de fraudes por ti.',
        },
      },
    },
    dashboard: {
      topBadge: 'Características',
      header: 'Panel de Sphere',
      subHeading:
        'Los enlaces de pago de Sphere son versátiles, personalizables y robustos. Se adaptan a tus necesidades, ya sea para compras únicas o suscripciones recurrentes.',
      solutions: {
        '0': {
          title: 'Enlaces de pago',
          description:
            'Acepta pagos directamente desde email, SMS, redes sociales, sitio web o aplicación. Disfruta de todo el poder de Sphere sin tener que escribir una sola línea de código.',
        },
        '1': {
          title: 'Suscripciones',
          description:
            'Ofrece suscripciones tradicionales o auténticas on-chain—directamente desde tarjeta, cuenta bancaria o billetera digital. Facturación flexible desde por minuto hasta anual, medida por asiento, y de volumen a niveles graduados. Recibe pagos directamente, sin intermediarios, unwrapping de tokens o relays de gas innecesarios.',
        },
        '2': {
          title: 'Herramientas de desarrollo',
          description:
            'Sphere simplifica tu proceso de ingeniería con funciones de pago integradas. Nos encargamos de las complejidades económicas globales, permitiendo que tus equipos se enfoquen en un desarrollo sin interrupciones en una sola plataforma.',
        },
        '3': {
          title: 'Análisis e insights de clientes',
          description:
            'Monitorea más de 15 KPIs, como MRR, churn, tasas de conversión y clics. Maximiza tus ingresos, aumenta las tasas de aprobación, recibe pagos más rápido y reduce contracargos aceptando una mayor variedad de pagos.',
        },
      },
    },
    developerTools: {
      topBadge: 'SDK',
      header: 'Kit de herramientas para desarrolladores',
      subHeading:
        'Aplicamos las mejores prácticas en pagos para ofrecer SDKs semánticos y fáciles de usar. Enfócate en hacer crecer tu negocio mientras nosotros nos encargamos de las auditorías de contratos inteligentes, la orquestación cross-chain y todo el mantenimiento técnico, operativo y de cumplimiento.',
      bullets: {
        '0': 'API RESTful simple, poderosa e intuitiva',
        '1': 'Código para copiar y pegar que simplemente funciona',
        '2': 'Documentación concisa y completa',
      },
      ctas: {
        primary: 'Documentación',
        secondary: 'Soporte al desarrollador',
      },
    },
    features: {
      header: {
        topBadge: 'Características',
        title: 'Una suite completa de pagos',
        description:
          'Conéctate de manera fluida a través de una única API o un panel fácil de usar en minutos.\nObtén acceso a cumplimiento de primer nivel, contabilidad, pagos, on/off-ramps, análisis y herramientas para desarrolladores.',
      },
      grid: {
        '0': 'Análisis completos',
        '1': 'Pagos y cobros multipartes',
        '2': 'Construcción colaborativa con ACLs basados en roles',
        '3': 'Detección de AML y fraude',
        '4': 'Seguro y certificado',
        '5': 'Notificaciones por email',
      },
      securityMarquees: {
        '0': 'OFAC\nsanciones',
        '1': 'Prácticas de mejor seguridad de FTC',
        '2': 'Prácticas de mejor seguridad de GDPR',
        '3': 'PCI\ncompliante',
      },

      benefits: {
        lowestFees: {
          title: 'Las tarifas más bajas del mercado',
          description:
            'Todas las transacciones con tarifas igualadas a las de la competencia, con las tarifas de red trasladadas al costo.',
        },
        convenientSwaps: {
          title: 'Intercambios convenientes',
          description:
            'Pagos directamente a tu cuenta bancaria o a la de un proveedor externo, de forma manual o programada, con tarifas competitivas a nivel OTC.',
        },
        instantSettlements: {
          title: 'Liquidaciones instantáneas',
          description:
            'Reciba su dinero en segundos o minutos, en lugar de días o semanas.',
        },
        splitPayouts: {
          title: 'Pagos divididos',
          description:
            'Divida los pagos automáticamente sin tarifas adicionales.',
        },
        nonCustodial: {
          title: 'No custodial',
          description:
            'Los pagos son de persona a persona y nunca manejamos fondos de clientes o comerciantes.',
        },
        audited: {
          title: 'Auditado',
          description:
            'Nuestros contratos son auditados por los mejores expertos de la industria. Regularmente realizamos modelado de amenazas, pruebas de penetración y evaluaciones de vulnerabilidades en nuestra infraestructura.',
        },
        compliant: {
          title: 'Compliance',
          description:
            'Sphere sigue todas las mejores prácticas según las regulaciones de EE.UU. y utiliza transmisores de dinero con licencia.',
        },
        encrypted: {
          title: 'Cifrado',
          description:
            'Todos los datos están cifrados y/o tokenizados en reposo y en tránsito. Nunca almacenamos PII sensible y tenemos ACLs estrictos.',
        },
      },
    },
    ctaSection: {
      header: '¿Listo para dar el siguiente paso?',
      description:
        'Forma parte de los líderes de la industria que ya están creciendo con Sphere.',
      button: 'Comenzar',
    },
    faq: {
      header: 'FAQ',
      description:
        'Preguntas frecuentes. ¿No encuentras la respuesta que buscas? Ponte en contacto con nosotros.',
      questions: {
        '0': {
          q: '¿Cuánto cuesta Sphere?',
          a: 'El acceso al software es gratuito, pero cobramos una pequeña tarifa por transacción. Precios personalizados para empresas están disponibles a pedido.',
        },
        '1': {
          q: '¿Qué redes son compatibles?',
          a: 'Compatibilizamos con todas las principales redes de tarjetas (Visa, Mastercard, American Express, Discover) en más de 120 países, transferencias bancarias y transferencias nacionales e internacionales.',
        },
        '2': {
          q: '¿Cómo se maneja la conversión de divisas?',
          a: 'Los clientes consienten en cambiar a una moneda diferente durante la transferencia, que se enruta a través de un intercambio o está sujeta a tasas de cambio al costo.',
        },
        '3': {
          q: '¿Sphere es auditado?',
          a: 'Sí, somos auditados por OtterSec y cumplimos con PCI-DSS. Nos ofrecemos voluntariamente para cumplir con los requisitos de MSB/VASP y empleamos un programa sólido de KYC/AML. Estamos en proceso de recibir nuestras certificaciones SOC2 e ISO/IEC 27001.',
        },
        '4': {
          q: '¿Sphere custodia fondos?',
          a: 'Nunca. Somos un proveedor de servicios tecnológicos, no un MSB/VASP ni un transmisor de dinero. Todos los pagos que implican custodia son manejados por transmisores de dinero con licencia, custodios calificados y bancos con licencia.',
        },
        '5': {
          q: '¿Es posible integrar Sphere con mi solución de pago existente?',
          a: 'Sí. Puedes usar tu propio Stripe u otros proveedores de pago, y usar Sphere para agregar soporte para pagos alternativos y automatización de back-office a través de nuestra API/SDK. Diseñamos nuestra API para usar flujos de datos y modelos de objetos similares para reducir la cantidad de infraestructura que se debe reescribir.',
        },
      },
    },
    footer: {
      slogan:
        'Desde dólares y euros hasta pesos y más, Sphere hace que moverse entre monedas sea sencillo.',
      termsOfService: 'Términos de Servicio & Política de Privacidad',
      privacyPolicy: 'Política de Privacidad',
      reportVulnerability: 'Reportar una vulnerabilidad',
    },
  },
  accessRequest: {
    steps: {
      '1': {
        title: 'Solicitar Acceso',
        descriptionParagraph1:
          'Para <0>individuos</0> que desean utilizar nuestra funcionalidad de rampa, ahora pueden hacerlo sin necesidad de contactarnos. <0>Simplemente visita</0> <1>spherepay.co/ramp</1> para registrarte y comenzar.',
        descriptionParagraph2:
          'Para empresas u otras solicitudes, por favor completa la información a continuación y un experto de Sphere se pondrá en contacto contigo pronto.',
        form: {
          walletAddress: 'Dirección de la Billetera',
          firstName: 'Nombre',
          lastName: 'Apellido',
          email: 'Correo Electrónico',
          businessName: 'Nombre de la Empresa',
          businessUrl: 'URL del Sitio Web de la Empresa',
          phoneNumber: 'Número de Teléfono',
        },
        notYourEmail: '¿No es tu correo?',
        errors: {
          emailAlreadyInUse:
            'Este correo electrónico ya está en uso para una solicitud activa. Por favor, intenta con otro.',
          emailAlreadyLinkedToAccount:
            'Este correo electrónico ya está vinculado a una cuenta. ¿Prefieres iniciar sesión?',
        },
      },
      '2': {
        title: 'Información de Contacto',
        form: {
          preferredContactMethod: 'Método de Contacto Preferido',
          preferredContactMethodDescription:
            'Los detalles de acceso a la cuenta se enviarán por correo electrónico.',
          placeholders: {
            telegram: '@nombredeusuario',
            whatsapp: '+1234567890',
            email: 'tu@ejemplo.com',
            sms: '+1234567890',
          },
        },
      },
      '3': {
        title: 'Detalles del Negocio',
        form: {
          bankAccountJurisdiction:
            '¿Su negocio tiene una cuenta bancaria en algún de los siguientes países?',
          bankAccountJurisdictionsOptions: {
            unitedStates: 'EE.UU. (USD)',
            europeanUnion: 'Unión Europea (EUR)',
            noneOfTheAbove: 'Ninguna de las opciones anteriores',
          },
          preferredCurrencies:
            '¿Qué monedas desea utilizar para transacciones de entrada y salida?',
          preferredCurrenciesDescription: 'Por favor, seleccione al menos una.',
          otherCurrencyPlaceholder: 'Por favor, especifique',
        },
      },
      '4': {
        title: 'Detalles del Negocio',
        submitApplication: 'Enviar solicitud',
        form: {
          businessOperatingAddress: 'Dirección operativa del negocio',
          businessCountry: '¿En qué país está ubicado su negocio?',
          countryPlaceholder: 'País',
          statePlaceholder: 'Estado',
          monthlyTransferVolume: 'Volumen de transferencia mensual',
          estimatedTransactionVolume:
            '¿Cuál fue el volumen de transacciones estimado del mes pasado?',
          selectPlaceholder: 'Por favor seleccione',
          monthlyVolume: {
            '0_4999': 'Menos de $4,999',
            '5000_9999': '$5,000 - $9,999',
            '10000_99999': '$10,000 - $99,999',
            '100000_499999': '$100,000 - $499,999',
            '500000_999999': '$500,000 - $999,999',
            '1000000_5000000': '$1,000,000 - $5,000,000',
            '5000000_plus': 'Más de $5,000,000',
          },
          additionalDetails: 'Detalles adicionales',
          additionalDetailsDescription:
            'Describa la naturaleza del negocio y sus objetivos para la cuenta.',
        },
      },
    },
    submitted: {
      title: 'Tu registro ha sido\nenviado con éxito',
      defaultBody:
        'Gracias por tu interés. Nuestro equipo revisará tu información y se pondrá en contacto contigo <0>por correo electrónico</0> si podemos otorgarte acceso a una cuenta.',
      bridgeBody:
        'Gracias por tu interés. Nuestro equipo revisará tu información y se pondrá en contacto contigo <0>por correo electrónico</0> cuando podamos atenderte.',
      conciergeBody:
        '¡Excelente! Alguien de nuestro equipo se pondrá en contacto contigo pronto. Si hay algún retraso, no dudes en contactarnos directamente aquí:',
      returnToWebsite: 'Volver al sitio web',
      contactUs: 'Conectarse con su especialista de onboarding',
    },
    googleSplash: {
      pendingTitle: 'Tu cuenta aún está\npendiente de aprobación',
      noAccountTitle: 'Este Gmail no está asociado\ncon una cuenta de Sphere',
      applicationId: 'Tu ID de Aplicación:',
      pendingDescription:
        'Hemos recibido tu solicitud de Sphere y estamos procesando tu petición. Nos pondremos en contacto contigo pronto con una actualización sobre el estado de tu cuenta lo antes posible.',
      noAccountDescription:
        'Este Gmail no está vinculado a una cuenta de Sphere.\nCompleta una solicitud para comenzar.',
      returnToLogin: 'Volver al inicio de sesión',
      fillOutApplication: 'Completar solicitud',
    },
    loadingAnimation: {
      calculatingRates: 'Calculando las mejores tasas de cambio...',
      securingTransactions: 'Asegurando tus transacciones...',
      optimizingPayments: 'Optimizando pagos globales...',
      connectingCurrencies: 'Conectando monedas...',
      ensuringCompliance: 'Garantizando estándares de cumplimiento...',
      processingData: 'Procesando datos de forma segura...',
      almostThere: 'Ya casi estamos...',
    },
    passwordSetup: {
      loginSuccess: '¡Inicio de sesión exitoso! Bienvenido de nuevo {{email}}',
      newLinkSent: 'Se ha enviado un nuevo enlace a tu correo electrónico.',
      regenerateError:
        'Ocurrió un error al regenerar el token de acceso. Por favor, inténtalo de nuevo.',
      tokenNotFoundOrInvalid: 'Token no encontrado o inválido',
      tokenNotFoundOrInvalidDescription:
        'El token de acceso que estás intentando usar no se encuentra o es inválido. Esto puede deberse a que ha expirado o a un error en el enlace.',
      returnToWebsite: 'Volver al sitio web',
      contactSupport: 'Contactar soporte',
      linkExpired: 'Tu enlace ha expirado',
      linkExpiredDescription:
        'El enlace de correo electrónico ha expirado. Por favor, genera uno nuevo para completar tu registro.',
      redirecting_zero: 'Redirigiendo ahora...',
      redirecting_one: 'Redirigiendo en 1 segundo...',
      redirecting_other: 'Redirigiendo en {{count}} segundos...',
      regenerateLink: 'Regenerar enlace',
      createSignIn: 'Crear Inicio de Sesión',
      createAccount: 'Crear cuenta',
      email: 'Correo electrónico',
      password: 'Contraseña',
      enterPassword: 'Ingresa la Contraseña',
      confirmPassword: 'Confirma la Contraseña',
    },
  },
  validation: {
    required: 'Requerido',
    invalidEmail: 'Por favor, introduce un correo electrónico válido',
    emailTooLong: 'El correo electrónico debe tener menos de 255 caracteres',
    firstNameTooLong: 'El nombre debe tener menos de 50 caracteres',
    lastNameTooLong: 'El apellido debe tener menos de 50 caracteres',
    businessNameTooLong:
      'El nombre de la empresa debe tener menos de 50 caracteres',
    businessUrlTooLong:
      'La URL del sitio web de la empresa debe tener menos de 255 caracteres',
    phoneNumberTooLong:
      'El número de teléfono debe tener menos de 20 caracteres',
    phoneNumberInvalid:
      'Por favor, introduce un número de teléfono válido en formato internacional (ej., +1234567890)',
    preferredContactMethodValueTooLong:
      'El valor del método de contacto preferido debe tener menos de 255 caracteres',
    weakPassword:
      'La contraseña debe tener al menos 6 caracteres y contener al menos una letra y un número',
    passwordsDoNotMatch: 'Las contraseñas no coinciden',
  },
  general: {
    comingSoon: 'Próximamente',
    support: 'Soporte',
    optional: 'Opcional',
    back: 'Volver',
    next: 'Siguiente',
    continue: 'Continuar',
    yes: 'Sí',
    no: 'No',
    other: 'Otro',
    selectAllThatApply: 'Selecciona todas las que se aplican',
    unknownError:
      'Ocurrió un error desconocido. Por favor, inténtalo de nuevo más tarde.',
  },
  dashboard: {
    drawer: {
      createButton: 'Crear',
      home: 'Inicio',
      wallets: 'Billeteras',
      transfers: {
        title: 'Transferencias',
        bankAccounts: 'Cuentas Bancarias',
      },
      payments: {
        title: 'Pagos',
        payment: 'Enlaces de pago',
        subscriptions: 'Suscripciones',
        customers: 'Clientes',
      },
      products: 'Productos',
      settings: 'Configuración',
      addName: 'Agregar Nombre',
      addEmail: 'Agregar Email',
      setupAccountButton: 'Configurar cuenta',
    },
    general: {
      logoutButton: 'Cerrar sesión',
      exportButton: 'Exportar',
      pagination: {
        rowsPerPage: 'Filas por página: ',
        previous: 'Anterior',
        next: 'Siguiente',
        showingEntries:
          'Mostrando {{entriesRangeStart}}-{{entriesRangeEnd}} de {{total}} entradas',
      },
      moreInfoButton: 'Más información',
      loadMore: 'Cargar más',
      edit: 'Editar',
      yes: 'Sí',
      no: 'No',
      off: 'Apagado',
      cancel: 'Cancelar',
      saveAndAttach: 'Guardar y adjuntar',
      hidePreview: 'Ocultar vista previa',
    },
    pages: {
      home: {
        mobile: {
          title:
            'El panel de control de Sphere no está disponible actualmente para móviles.',
          description:
            'El panel de control empresarial de Sphere no está disponible actualmente en móviles. Por favor, cambie a una computadora de escritorio para obtener toda la funcionalidad. Si no es una empresa y solo necesita hacer on- o off-ramp en móvil, use el enlace de Sphere Ramp a continuación.',
          sphereRampButton: 'Sphere Ramp',
          returnToWebsiteButton: 'Volver al sitio web',
        },
        title: 'Inicio',
        quillCharts: {
          grossVolume: {
            title: 'Volumen bruto',
          },
          loading: 'Cargando',
          description: 'vs {{dollarSign}}{{amount}} último período',
          subchartIdTitles: {
            [AMOUNT_OFF_RAMPED]: 'Cantidad Off-Ramped',
            [AMOUNT_ON_RAMPED]: 'Cantidad On-Ramped',
            [AVERAGE_SPEND_PER_CUSTOMER]: 'Gasto Promedio Por Cliente',
            [FAILED_PAYMENTS]: 'Pagos Fallidos',
            [MRR]: 'IMR',
            [NET_VOLUME]: 'Volumen Neto',
            [SUCCESSFUL_PAYMENTS]: 'Pagos Exitosos',
            customers: 'Clientes',
          },
        },
        quillDatePicker: {
          dateRange: 'Rango de Fechas',
          timePeriod: 'Período de Tiempo',
          or: 'O',
          from: 'Desde',
          to: 'Hasta',
          apply: 'Aplicar',
          timePeriods: {
            'This week': 'Esta semana',
            'This month': 'Este mes',
            'This year': 'Este año',
            'Last 7 days': 'Últimos 7 días',
            'Last 30 days': 'Últimos 30 días',
            'Last 90 days': 'Últimos 90 días',
            'Last 6 months': 'Últimos 6 meses',
            'All time': 'Todo el tiempo',
          },
          comparison: {
            PREV_PERIOD: 'Período Anterior',
            PREV_7_DAYS: 'Anteriores 7 días',
            PREV_30_DAYS: 'Anteriores 30 días',
            PREV_90_DAYS: 'Anteriores 90 días',
            NO_COMPARISON: 'Sin comparación',
          },
        },
      },
      wallets: {
        title: 'Billeteras',
        walletAdresses: {
          title: 'Direcciones de billeteras',
          subtitle:
            'Las billeteras son direcciones custodiadas o no custodiadas en redes blockchain que se utilizan para almacenar monedas digitales.',
          walletsAccordionButtonFirstWallet: 'Agregar una billetera',
          walletsAccordionButton: 'Agregar otra billetera',
        },
      },
      transfers: {
        title: 'Transferencias',
        transfers: {
          tabs: {
            transfersSummary: 'Resumen de transferencias',
            bankAccounts: 'Cuentas bancarias',
          },
          bankAccounts: {
            generateOffloadWallet: {
              title: 'Genera tu primera billetera Offload',
              description:
                'Crea una dirección de billetera vinculada a tu cuenta bancaria. Esto convertirá automáticamente todos los fondos enviados a esta dirección y los depositará directamente en tu cuenta bancaria.',
              action: 'Cerrar',
              bannerInfo:
                'Nota: Se ha introducido una tarifa estándar del 0,2 % para las transacciones enviadas a billeteras Offload.',
            },
          },
        },
      },
      payments: {
        title: 'Pagos',
        tabs: {
          paymentsSummary: 'Resumen de pagos',
          paymentLinks: 'Enlaces de pago',
          subscriptions: 'Suscripciones',
          customers: 'Clientes',
        },
        cards: {
          totalRevenue: 'Ingresos Totales:',
          customers: 'Clientes:',
          activeSubscribers: 'Suscriptores Activos:',
        },
        paymentsSummary: {
          title: 'Pagos Recientes',
          tabs: {
            all: 'Todos',
            succeeded: 'Exitosos',
            failed: 'Fallidos',
          },
          searchbarPlaceholder: 'Buscar por ID, cliente o producto',
          actions: {
            createNewPayment: 'Crear nuevo pago',
            moreDetails: 'Más detalles',
          },
          content: {
            empty: {
              title: 'No se encontraron pagos',
              description:
                'Comienza creando un producto y asignándolo a un método de pago.',
            },
            table: {
              id: 'ID Sphere',
              paymentType: 'Tipo',
              status: 'Estado',
              network: 'Red',
              amount: 'Monto',
              customer: 'Cliente',
              product: 'Producto',
              date: 'Fecha',
              view: 'Ver',
            },
          },
        },
        paymentDetails: {
          headings: {
            paymentDetails: 'Detalles de Pago',
            tax: 'Impuesto',
            timeline: 'Cronograma / Línea de tiempo',
            products: 'Productos',
            metadata: 'Metadatos',
          },
          labels: {
            receipt: 'Recibo',
            created: 'Creado',
            customer: 'Cliente',
            paymentMethod: 'Método de pago',
            type: 'Tipo',
            amount: 'Cantidad',
            referenceId: 'ID de referencia',
            id: 'ID',
            description: 'Descripción',
            transaction: 'Transacción',
            card: 'Tarjeta',
            subscription: 'Suscripción',
            shippingAddress: 'Dirección de envío',
            taxLocation: 'Ubicación de Impuesto',
            taxAmount: 'Monto de Impuesto',
            noTaxRatesFound: 'No se encontraron tasas de impuestos',
            active: 'Activo',
            noEvents: 'Sin eventos',
            noProductsFound: 'No se encontraron productos',
            quantity: 'Cantidad',
            billed: 'Facturado',
          },
        },
        paymentLinks: {
          paymentLinks: {
            title: 'Enlaces de pago',
            description:
              'Los enlaces de pago permiten a los clientes pagar productos por única vez o de manera recurrente a través de páginas de pago prediseñadas.',
            action: 'Crear nuevo enlace de pago',
          },
          cards: {
            activeLinkRevenue: 'Ingresos de Enlaces Activos',
            activeLinks: 'Enlaces Activos',
          },
          tabs: {
            all: {
              content: {
                empty: {
                  title: 'No se encontraron enlaces de pago',
                  description:
                    'Vincula productos a enlaces de pago para vender tus productos y/o servicios en línea.',
                },
                table: {
                  id: 'Id Sphere',
                  name: 'Nombre',
                  hostedUrl: 'URL alojada',
                  price: 'Precio',
                  status: 'Estado',
                  created: 'Creado',
                  actions: 'Acciones',
                  actionsDropdown: {
                    view: 'Detalles',
                    edit: 'Editar',
                    delete: 'Borrar',
                    confirmDelete: 'Eliminar el enlace de pago de ',
                  },
                },
              },
            },
          },
          preview: {
            title: 'Vista previa',
            tabs: {
              desktop: 'Escritorio',
              mobile: 'Móvil',
            },
          },
          details: {
            header: {
              for: 'por',
              description:
                'Copia y comparte para comenzar a aceptar pagos con este enlace',
              link: 'Enlace:',
            },
            metrics: {
              created: 'Creado',
              grossVolume: 'Volumen bruto',
              payments: 'Pagos',
            },
            products: {
              title: 'Productos',
              empty: 'No se encontraron productos',
              quantity: 'Cantidad: {{amount}}',
            },
            paymentLinkDetails: {
              title: 'Detalles del enlace de pago',
              default: 'Por defecto',
              status: 'Estado',
              dateCreated: 'Fecha de creación',
              allowPromotionCodes: 'Permitir códigos de promoción',
              confirmationPage: 'Página de confirmación',
              deactivateLinkPage: 'Página de enlace desactivada',
              splitPayouts: 'Pagos divididos',
              phases: {
                title: 'Fases',
                phase: 'Fase',
                supply: 'Suministro: {{amount}}',
                dateRange: 'Rango de fechas: ',
                discordGated: 'Discord cerrado:',
              },
            },
            paymentMethods: {
              title: 'Métodos de pago',
            },
            paymentHistory: {
              title: 'Historial de pagos',
              description: 'Ver todos los pagos para este enlace de pago',
              empty: 'No se encontraron pagos',
              table: {
                status: 'Estado',
                amount: 'Gasto total',
                created: 'Creado',
                actions: '',
                customer: 'Cliente',
                paymentMethod: 'Billetera',
                view: 'Detalles',
              },
            },
            metadata: {
              title: 'Metadatos',
              key: 'Clave',
              value: 'Valor',
              empty: 'No se encontraron metadatos',
              actions: {
                add: '+ Agregar otro elemento',
                cancel: 'Cancelar',
                save: 'Ahorrar',
              },
            },
          },
        },
        subscriptions: {
          subscriptions: {
            title: 'Suscripciones',
            description:
              'Recibe pagos por productos periódicamente mediante una suscripción. Estas pueden ser en custodia o delegadas.',
            action: 'Crear enlace de pago recurrente',
          },
          tabs: {
            subscriptions: 'Suscripciones',
            recurringPaymentLinks: 'Enlaces de pago recurrente',
            subscriptionsTabs: {
              all: 'Todas',
              active: 'Activas',
              pending: 'Pendientes',
              pastDue: 'Vencidas',
              incomplete: 'Incompletas',
              canceled: 'Canceladas',
            },
            content: {
              subscriptions: {
                empty: {
                  title: 'No se encontraron suscripciones',
                  description:
                    '¡Elige entre métodos de facturación con o sin custodia!',
                  action: 'Más información',
                  descriptionAlternative:
                    'Vincula productos a enlaces de pago para vender tus productos y/o servicios en línea.',
                  createNewSubscription: 'Crear nueva suscripción',
                  createNewPaymentLink: 'Crear enlace de pago',
                },
                table: {
                  id: 'Id Sphere',
                  status: 'Estado',
                  network: 'Red',
                  billedPerPeriod: 'Facturado por período',
                  customer: 'Cliente',
                  nextBillingIn: 'Próxima factura en',
                  currentFunding: 'Fondos actuales',
                  lifetimeBilled: 'Total facturado',
                  created: 'Creado',
                  view: 'Ver',
                },
              },
            },
          },
          durations: {
            billedStatement: 'Facturado {{duration}}',
            multiple: 'Cada {{count}} {{timePeriod}}',
            single: 'por {{timePeriod}}',
            timePeriods: {
              month: 'mes',
              months: 'meses',
              year: 'año',
              years: 'años',
              week: 'semana',
              weeks: 'semanas',
              day: 'día',
              days: 'días',
              min: 'minuto',
              mins: 'minutos',
            },
          },
          details: {
            subtitle:
              'La cuenta de tokens que financia esta suscripción tiene ',
            subscriptionDetails: {
              heading: 'Detalles de suscripción',
              ownerWallet: 'Cartera del propietario',
              fundingTokenAccount: 'Financiamiento de cuenta de token',
              customerID: 'ID de cliente',
              customerName: 'Nombre del cliente',
              customerEmail: 'Correo electrónico del cliente',
              currency: 'Divisa',
              products: 'Productos',
              lastBilling: 'Última facturación:',
              billingRetries: 'Reintentos de facturación:',
              cancellationReason: 'Motivo de cancelación:',
              created: 'Creado',
              updated: 'Actualizado',
            },
            timeline: {
              heading: 'Cronograma del evento',
              empty: 'Sin eventos',
            },
          },
        },
        customers: {
          cards: {
            customers: 'Clientes',
            activeSubscribers: 'Suscriptores activos',
          },
          tabs: {
            all: 'Todos',
          },
          content: {
            searchbarPlaceholder: 'Buscar por ID, nombre, cartera o correo',
            empty: {
              title: 'No se encontraron clientes',
            },
            table: {
              id: 'ID #',
              customer: 'Cliente',
              wallet: 'Cartera',
              created: 'Creado',
              view: 'Ver',
            },
          },
          details: {
            tabs: {
              overview: 'Descripción general',
              wallets: 'Carteras',
            },
            insights: {
              heading: 'Perspectivas',
              stats: {
                spend: 'Gastar',
                volume: 'Volumen de transferencia',
                since: 'Desde',
                mrr: 'MRR',
              },
            },

            subscriptions: {
              heading: 'Suscripciones',
              statusMessages: {
                [SubscriptionStatus.PENDING]: `Próximo cargo pendiente el {{nextBillingDate}} por {{averageAmountBilledPerPeriodFixed}} {{tokenSymbol}}`,
                [SubscriptionStatus.ACTIVE]: `El próximo cargo será el {{nextBillingDate}} por {{averageAmountBilledPerPeriodFixed}} {{tokenSymbol}}`,
                [SubscriptionStatus.PAST_DUE]: `Cargo no realizado el {{nextBillingDate}} por {{averageAmountBilledPerPeriodFixed}} {{tokenSymbol}}`,
                [SubscriptionStatus.CANCELED]: `Cancelado el {{date}}`,
                [SubscriptionStatus.INCOMPLETE]: `Cargo incompleto el {{nextBillingDate}} por {{averageAmountBilledPerPeriodFixed}} {{tokenSymbol}}`,
              },
              escrowed: 'En depósito en garantía',
              escrowless: 'Sin depósito en garantía',
              view: 'Detalles',
              empty: 'No se encontraron suscripciones',
            },
            accountDetails: {
              heading: 'Detalles de la cuenta',
              customerType: 'Tipo de cliente',
              personal: 'Personal',
              business: 'Negocio',
              kybStatus: 'Estado KYB',
              owner: 'Dueño',
              name: 'Nombre',
              kycStatus: 'Estado KYC',
              kycbLink: 'Enlace KYC/B',
              resetLink: 'Restablecer enlace',
              generateLink: 'Generar enlace',
              email: 'Correo electrónico',
              shippingDetails: 'Detalles de envío',
            },
            paymentMethods: {
              heading: 'Métodos de pago',
              empty: 'No se encontraron métodos de pago',
            },
            paymentHistory: {
              heading: 'Historial de pagos',
              description: 'Ver todos los pagos de este cliente',
              empty: 'No se encontraron pagos',
              table: {
                created: 'Fecha',
                amount: 'Cantidad',
                type: 'Tipo',
                name: 'Nombre',
                paymentMethod: 'Método de pago',
                status: 'Estado',
                actions: '',
                view: 'Detalles',
              },
            },
            transferHistory: {
              heading: 'Historial de transferencias',
              description: 'Ver todas las transferencias a este cliente',
              empty: 'No se encontraron transferencias',
              table: {
                created: 'Fecha',
                amount: 'Cantidad',
                type: 'Tipo',
                name: 'Nombre',
                source: 'Transferir desde',
                destination: 'Transferir a',
                status: 'Estado',
                actions: '',
                view: 'Detalles',
              },
            },
          },
        },
      },
      products: {
        title: 'Productos',
        products: {
          title: 'Productos',
          description:
            'Los productos son bienes o servicios específicos que ofreces para la venta. Un producto puede ser un artículo físico, un producto digital o un servicio por suscripción. ¡Asocia un producto a un método de pago para empezar a vender!',
          actions: 'Crear nuevo producto',
        },
        cards: {
          productSales: 'Ventas de productos:',
          topProduct: {
            title: 'Producto destacado:',
            contentEmpty: 'Ninguno',
          },
        },
        tabs: {
          all: 'Todos',
          oneTime: 'Pago por única vez',
          recurring: 'Recurrente',
        },
        content: {
          empty: {
            title: 'No se encontraron productos',
            description: 'Crea un producto y asígnalo a un método de pago.',
            action: 'Crear nuevo producto',
          },
          table: {
            productName: 'Nombre del producto',
            tags: 'Etiquetas',
            paymentType: 'Tipo de pago',
            price: 'Precio',
            modified: 'Modificado',
            actions: 'Acciones',
            actionsDropdown: {
              view: 'Detalles',
              edit: 'Editar',
            },
          },
        },
        details: {
          pricing: 'Precios',
          id: 'ID:',
          network: 'Red:',
          amount: 'Cantidad:',
          type: 'Tipo:',
          billing: 'Facturación:',
          metered: 'Medido:',
          collapse: 'Colapsar >',
          showMore: 'Mostrar más >',
          metadata: 'Metadatos',
          eventData: 'Datos del evento',
        },
        billingScheme: {
          perUnit: 'por unidad',
          tiered: 'escalonado',
        },
      },
      developers: {
        title: 'Desarrolladores',
        searchbarPlaceholder: 'Ver documentación',
        tabs: {
          apiKeys: 'Clave Api',
          webhooks: 'Webhooks',
          events: 'Eventos',
          requests: 'Solicitudes',
        },
        apiKeys: {
          title: 'Clave Api',
          description:
            'Estas claves te permitirán autenticar solicitudes a la API.',
          action: 'Crear nueva clave API',
          content: {
            empty: {
              title: 'No se encontraron claves API',
              description: 'No has creado ninguna clave API.',
            },
            table: {
              name: 'Nombre',
              note: 'Nota',
              created: 'Creado',
              actions: 'Acciones',
              actionsDropdown: {
                edit: 'Editar',
                delete: 'Borrar',
                deleteConfirm: {
                  title: 'Eliminar clave API',
                  message:
                    'Está a punto de eliminar su clave API. No podrá recuperarla una vez eliminada. Confirme a continuación.',
                },
                deleteSuccess: 'Clave API: {{name}} eliminada exitosamente.',
                deleteFailure: 'No se pudo eliminar.',
              },
            },
          },
          form: {
            name: {
              label: 'Nombre',
              placeholder: 'Introduzca el nombre de la clave',
            },
            info: {
              title: 'Manteniendo su llave segura',
              description:
                'Asegúrate de guardar esta clave en un lugar seguro, no podrás volver a copiarla.',
            },
            note: {
              label: 'Nota',
              placeholder: 'Añade una nota sobre esta clave aquí',
            },
            action: 'Hecho',
            success: {
              message:
                'Tu clave API se ha creado correctamente. Puedes consultar su estado en el panel de desarrollador.',
              title: 'Clave API creada',
              copySuccess: '¡Clave API copiada!',
              info: {
                title: 'Mantén tu llave segura',
                description:
                  'Asegúrate de guardar esta clave en un lugar seguro, no podrás copiarla ni verla nuevamente.',
              },
              actions: {
                create: 'Crear otra clave',
                return: 'Volver al panel',
              },
            },
            toast: {
              success: 'Clave API actualizada',
              error: 'Crear otra clave',
            },
          },
        },
        webhooks: {
          title: 'Webhooks',
          description:
            'Los webhooks te permiten propagar eventos de Sphere a tus servicios backend vía HTTP.',
          action: 'Crear webhook',
          content: {
            empty: {
              title: 'No se encontraron webhooks',
              description: 'No has creado ningún webhook.',
            },
            table: {
              url: 'URL',
              signingSecret: 'Secreto de firma',
              rate: 'Tasa',
              created: 'Creado',
              status: 'Estado',
              enabled: 'Habilitado',
              actions: 'Acciones',
              actionsDropdown: {
                view: 'Detalles',
                edit: 'Editar',
                delete: 'Borrar',
                revealSecret: 'Revelar',
                deleteConfirm: {
                  title: 'Eliminar webhook',
                  message:
                    'Estás a punto de eliminar tu webhook. Confirma a continuación.',
                },
                deleteSuccess: 'Webhook: {{name}} eliminado exitosamente.',
                deleteFailure: 'No se pudo eliminar',
                copySuccess: '¡Firma secreta copiada!',
              },
            },
            info: {
              description: 'Descripción: ',
              events: 'Eventos: ',
            },
          },
          form: {
            title: {
              create: 'Crear un webhook',
              update: 'Actualizar webhook',
            },
            description:
              'Selecciona dónde escuchar eventos y activar funciones específicas. ',
            learnMore: 'Aprende más',
            fields: {
              hookUrl: {
                label: 'URL del webhook',
                placeholder: '{{frontendUrl}}/webhook',
                error: {
                  required: 'La URL del webhook es requerida',
                  invalid: 'URL inválida',
                },
              },
              description: {
                label: 'Descripción',
                placeholder: 'Añade una descripción opcional aquí...',
              },
              events: {
                label: 'Selecciona eventos para escuchar:',
                error: {
                  required: 'Se requiere al menos 1 evento',
                },
              },
            },
            sections: {
              address: {
                label: 'Dirección',
                events: {
                  created: 'Dirección Creada',
                },
              },
              alert: {
                label: 'Alerta',
                events: {
                  created: 'Alerta Creada',
                },
              },
              customer: {
                label: 'Cliente',
                events: {
                  created: 'Cliente Creado',
                  updated: 'Cliente Actualizado',
                  deleted: 'Cliente Eliminado',
                  tosPending: 'Cliente TOS Pendiente',
                  tosSuccess: 'Cliente TOS Exitoso',
                  kycPending: 'Cliente KYC Pendiente',
                  kycActionRequired: 'Cliente KYC Acción Requerida',
                  kycSuccess: 'Cliente KYC Exitoso',
                  kycRejected: 'Cliente KYC Rechazado',
                },
              },
              wallet: {
                label: 'Billetera',
                events: {
                  created: 'Billetera Creada',
                },
              },
              bankAccount: {
                label: 'Cuenta Bancaria',
                events: {
                  created: 'Cuenta Bancaria Creada',
                  updated: 'Cuenta Bancaria Actualizada',
                },
              },
              payout: {
                label: 'Pago',
                events: {
                  created: 'Pago Creado',
                  pending: 'Pago Pendiente',
                  funded: 'Pago Financiado',
                  successful: 'Pago Exitoso',
                  cancelled: 'Pago Cancelado',
                  failed: 'Pago Fallido',
                  refunded: 'Pago Reembolsado',
                },
              },
              payment: {
                label: 'Pago',
                events: {
                  pending: 'Pago Pendiente',
                  failed: 'Pago Fallido',
                  successful: 'Pago Exitoso',
                },
              },
              subscription: {
                label: 'Suscripción',
                events: {
                  created: 'Suscripción Creada',
                  billed: 'Suscripción Facturada',
                  approved: 'Suscripción Aprobada',
                  pastDue: 'Suscripción Vencida',
                  cancelled: 'Suscripción Cancelada',
                  updated: 'Suscripción Actualizada',
                  redelegated: 'Suscripción Re-delegada',
                  lowDelegatedBalance: 'Suscripción Saldo Delegado Bajo',
                  lowEscrowedBalance: 'Suscripción Saldo en Custodia Bajo',
                  insufficientDelegatedBalance:
                    'Suscripción Saldo Delegado Insuficiente',
                  insufficientDelegatedApprovedBalance:
                    'Suscripción Saldo Delegado Aprobado Insuficiente',
                  insufficientEscrowedBalance:
                    'Suscripción Saldo en Custodia Insuficiente',
                },
              },
              paymentLink: {
                label: 'Enlace de Pago',
                events: {
                  created: 'Enlace de Pago Creado',
                  deleted: 'Enlace de Pago Eliminado',
                  updated: 'Enlace de Pago Actualizado',
                },
              },
              price: {
                label: 'Precio',
                events: {
                  created: 'Precio Creado',
                  deleted: 'Precio Eliminado',
                  updated: 'Precio Actualizado',
                },
              },
              product: {
                label: 'Producto',
                events: {
                  created: 'Producto Creado',
                  deleted: 'Producto Eliminado',
                  updated: 'Producto Actualizado',
                },
              },
              webhook: {
                label: 'Webhook',
                events: {
                  created: 'Crear Webhook',
                  deleted: 'Eliminar Webhook',
                  updated: 'Actualizar Webhook',
                },
              },
            },
            actions: {
              cancel: 'Cancelar',
              create: 'Crear webhook',
              update: 'Actualizar webhook',
            },
            toast: {
              success: {
                create: '¡Webhook creado!',
                update: '¡Webhook actualizado!',
              },
            },
          },
        },
        events: {
          title: 'Eventos',
          description:
            'Los eventos hacen un seguimiento y monitorean varias actividades y cambios que ocurren dentro de tu cuenta de Sphere.',
          content: {
            empty: {
              title: 'No se encontraron eventos',
              description: 'Los eventos aparecerán aquí cuando ocurran.',
            },
            table: {
              name: 'Nombre del evento',
              id: 'ID',
              created: 'Fecha de creación',
            },
          },
        },
        requests: {
          title: 'Solicitudes',
          description:
            'Las solicitudes son registros de auditoría del tráfico https entre tus servidores y Sphere.',
          requestsInfo: {
            badges: {
              ingress: 'Ingreso',
              egress: 'Egreso',
              ingressDescription: '(de tus servidores a Sphere)',
              egressDescription: '(de Sphere a tus servidores)',
            },
          },
          webhookResponseRequest: {
            api: 'API',
            id: 'ID',
            time: 'Tempo',
            ipAdress: 'Dirección IP',
            type: 'Tipo',
            idempotencyKey: 'Clave de idempotencia',
            origin: 'Origen',
            actions: {
              resend: 'Reenviar',
              requestBody: 'Cuerpo de la solicitud',
              responseBody: 'Cuerpo de la respuesta',
            },
          },
        },
      },
      settings: {
        title: 'Ajustes',
        tabs: {
          merchant: 'Comerciante',
          teamPermissions: 'Permisos del equipo',
        },
        merchant: {
          merchantDetails: {
            title: 'Detalles del comerciante',
            actions: {
              cancel: 'Cancelar',
              save: 'Guardar ajustes',
            },
            inputs: {
              name: {
                label: 'Nombre del comerciante',
                description:
                  'Actualizar el nombre o razón social asociada a esta cuenta.',
                placeholder:
                  'Nombre de la cuenta o tienda, ej., Manzanas de Anatoly',
              },
              logo: {
                label: 'Logo',
                description:
                  'Actualiza el logo de la cuenta para que aparezca en facturas y checkouts. Se recomiendan imágenes horizontales (400x600)*.',
                placeholder: {
                  title: 'Haz clic o arrastra y suelta para reemplazar',
                  description: '.jpg o .png',
                  activeDrag: 'Deja tu logo aquí...',
                },
              },
              notifications: {
                label: 'Correo electrónico para notificaciones',
                description:
                  'Actualizar qué correo electrónico recibe notificaciones.',
                placeholder: 'comerciante@spherepay.co',
                toggleFalse: 'Notificaciones desactivadas',
                toggleTrue: 'Notificaciones habilitadas',
                errorToast:
                  'Correo electrónico no válido o vacío. Ingrese un correo electrónico válido para activar las notificaciones.',
              },
              merchantApplication: {
                label: 'Aplicación del comerciante',
                description:
                  'El identificador único para tu cuenta de comerciante en Sphere.',
              },
              accountKycB: {
                label: 'KYC/B de la cuenta',
                description:
                  'El chequeo KYC/B es el proceso obligatorio para los clientes que deseen iniciar rampas de entrada y salida.',
                inputTitle: '{{kycb}} de comerciante',
                inputDescription: {
                  approved:
                    '¡La presentación del {{kycb}} del comerciante fue exitosa!',
                  pending:
                    'Su solicitud está en revisión. Espere un tiempo para que el proceso se complete.',
                  incomplete:
                    'Aún no has completado tu {{kycb}}. Para continuar, ve a la página de transferencias y sigue los pasos para finalizar el proceso.',
                  rejected:
                    'Lamentablemente, su solicitud de {{kycb}} fue rechazada. Por favor, revise los motivos y vuelva a enviarla.',
                  additionalReviewRequired:
                    'Su envío de {{kycb}} requiere una revisión adicional. Es posible que deba proporcionar más información o volver a enviar ciertos documentos.',
                },
                inputBadge: {
                  approved: 'Aprobado',
                  pending: 'Pendiente',
                  incomplete: 'Incompleto',
                  rejected: 'Rechazado',
                  additionalReviewRequired: 'Se requiere revisión adicional',
                },
                inputTag: { business: 'Negocio', individual: 'Personal' },
              },
              apiVersion: {
                label: 'Versión de la API',
                description:
                  'La versión de la API para tu aplicación si se excluye de los encabezados HTTP.',
              },
            },
          },
          merchantWallets: {
            title: 'Billeteras del comerciante',
            description:
              'Conecta múltiples billeteras a través de distintas redes a tu cuenta de Sphere. Estas billeteras se usan para recibir pagos de enlaces de pago.',
          },
        },
        teamPermissions: {
          title: 'Permisos del equipo',
          inputs: {
            inviteTeamMembers: {
              label: 'Invitar miembros del equipo',
              description:
                'Haz que tus proyectos despeguen más rápido invitando a tu equipo a colaborar.',
              placeholder: 'miembrodelequipo@spherepay.co',
              actions: {
                add: 'Agregar otro',
                invite: 'Invitar',
              },
            },
            teamMembers: {
              label: 'Miembros del equipo',
              description:
                'Administra tu equipo actual y cambia roles o permisos.',
              input: {
                table: {
                  identifier: 'Identificador',
                  role: 'Rol',
                  contentEmpty: 'Sin miembros',
                  content: {
                    action: 'Editar',
                  },
                },
              },
              delete: {
                action: 'Delete member',
                success: 'Member deleted successfully',
              },
            },
          },
        },
      },
      create: {
        title: 'Crear nuevo',
        transfer: {
          title: 'Transferencia',
          description:
            'Comienza a transferir entre fiat  dinero y stablecoins en unos simples pasos.',
        },
        product: {
          title: 'Producto',
          description:
            'Crea un producto para vender a tus clientes. Puede ser una compra única o una suscripción.',
        },
        paymentLink: {
          title: 'Enlace de pago',
          description:
            'Habilita pagos únicos o recurrentes generando enlaces que redirigen al checkout.',
        },
        subscription: {
          title: 'Suscripción',
          description:
            'Crea un enlace para acceder continuamente a un producto recurrente. Estos pueden ser en custodia o delegados.',
        },
      },
      createProductForm: {
        title: 'Crear Producto',
        titleEditing: 'Editar Producto',
        accordions: {
          details: {
            title: 'Detalles',
            inputs: {
              name: {
                label: 'Nombre',
                placeholder: 'Nombre del producto',
              },
              description: {
                label: 'Descripción',
                placeholder:
                  'Describe los bienes o servicios que proporciona este producto.',
              },
            },
          },
          pricing: {
            title: 'Precios',
            description:
              'Define los posibles precios para tu producto. Los precios no se pueden editar. Puedes adjuntar múltiples precios a un producto. Los precios se pueden eliminar si no están en uso actualmente.',
            action: 'Crear un nuevo precio',
          },
          images: {
            title: 'Imágenes',
            description:
              'Agrega hasta 6 imágenes a tu producto. Estas imágenes se mostrarán a los clientes durante el pago, en correos electrónicos, compartidos sociales, vistas previas de enlaces y más.',
            inputs: {
              uploadImage: {
                label: 'Haz clic o arrastra y suelta para reemplazar',
                description: '.jpg o .png',
                activeDrag: 'Deja tus imágenes aquí...',
              },
            },
          },
          additionalDetails: {
            title: 'Detalles adicionales',
            inputs: {
              label: 'Etiquetas del producto',
              tooltip: 'Solo puedes tener hasta 6 etiquetas',
            },
          },
        },
        actions: {
          cancel: 'Cancelar',
          create: 'Crear Producto',
          update: 'Actualizar Producto',
          preview: 'Mostrar Vista Previa',
        },
        errorMessages: {
          specifyPrice:
            'Debes especificar al menos un precio para este producto.',
        },
      },
      createPriceForm: {
        title: 'Crear un precio',
        actions: {
          cancel: 'Cancelar',
          create: 'Crear',
        },
        escrowedPretext:
          'El cliente pagará por adelantado al momento de pagar:',
        escrowedPosttext: `Los clientes depositarán en una cuenta de depósito en garantía generada un importe total equivalente a {{defaultLength}} ciclo(s){{isMetered}}. Los clientes prepagarán {{defaultLength}} {{interval}}{{postfix}} durante el proceso de compra y deberán mantener ese importe total en su billetera al momento de la compra. Pueden retirar o recargar la cuenta de depósito en garantía.`,
        escrowlessPretext:
          'El cliente aprueba previamente que se le cobre hasta:',
        escrowlessPosttext: `Los clientes aprobarán las cuotas de suscripción directamente desde su billetera por hasta {{defaultLength}} ciclo(s){{isMetered}}. Se les cobra el ciclo inicial por adelantado al finalizar la compra y no necesitan tener el resto del importe en su billetera para aprobar. Pueden ajustar los importes de aprobación en cualquier momento.`,
        metered: ', en función del uso esperado especificado',
        buttons: { perUnit: 'Fijo', volume: 'Volumen', graduated: 'Graduado' },
        network: 'Red',
        token: 'Simbólico',
        networkChangeDisabled:
          'No puedes cambiar la red o el token por este precio',
        amount: {
          label: 'Cantidad',
          placeholder: 'Ingrese el monto del precio (por ejemplo, 100)',
        },
        billingType: {
          label: 'Tipo de facturación',
          solanaWarning:
            'La facturación recurrente solo está disponible en la red Solana.',
          tokenWarning:
            'El token seleccionado actualmente no es compatible con la facturación recurrente.',
          paymentLinkWarning:
            'Solo puedes crear precios recurrentes para este enlace de pago, ya que está diseñado para suscripciones.',
          customUnitWarning:
            "La facturación recurrente no está disponible cuando la opción 'El cliente elige' está habilitada.",
        },
        recurringTypes: {
          delegated: 'Delegado',
          escrowed: 'En depósito en garantía',
        },
        priceTypes: {
          oneTimeFee: 'Tarifa única',
          recurring: 'Recurrente (suscripción)',
        },
        fundingType: {
          title: 'Tipo de financiación',
        },
        rebillEvery: 'Volver a facturar cada:',
        billingCycle: 'Ciclo(s) de facturación',
        table: {
          price: 'Precio',
          firstUnit: 'PRIMERA UNIDAD',
          lastUnit: 'ÚLTIMA UNIDAD',
          perUnit: 'POR UNIDAD',
          flatFee: 'TARIFA FIJA',
          unitRange: 'RANGO DE UNIDADES',
          addAnotherRow: 'Agregar otra fila',
        },
      },
      createPaymentLinkForm: {
        title: 'Crear enlace de pago',
        titleEditing: 'Editar enlace de pago',
        accordions: {
          products: {
            title: 'Productos',
            inputs: {
              placeholder: 'Buscar un producto existente',
            },
            actionsEmpty: '+ Agregar nuevo producto',
            actions: '+ Agregar otro producto',
          },
        },
        actions: {
          cancel: 'Cancelar',
          submitEmpty: 'Selecciona al menos un producto antes de continuar',
          submitEditing: 'Guardar cambios',
          submit: 'Crear enlace de pago',
          preview: 'Mostrar Vista Previa',
          addCustomField: 'Agregar Campo Personalizado',
          allowUserToSelectQuantity:
            'Permitir al usuario seleccionar la cantidad',
        },
        quantity: 'Cantidad',
        quantityLabel: 'Etiqueta de cantidad',
        customerDetailsOptions: {
          title: 'Datos del cliente requeridos',
          name: 'Nombre',
          email: 'Correo electrónico',
          shippingAddress: 'Dirección de envío',
          customFieldsHeading: 'Campos personalizados',
          labelNamePlaceholder: 'Nombre de la etiqueta',
        },
        customFieldTypes: {
          text: 'Texto',
          longText: 'Texto largo',
          number: 'Número',
        },
        errorMessages: {
          failed: 'Error al agregar producto',
          unableSelectProduct: 'No se puede seleccionar el producto',
          unableSetName: 'No se puede establecer el nombre, intente nuevamente',
          unableSetEmail:
            'No se puede establecer el correo electrónico, intente nuevamente',
          unableSetShippingAddress:
            'No se puede establecer la dirección de envío, intente nuevamente',
        },
        descriptiveText:
          'Crear un enlace de solicitud de pago que dirija a los clientes a la página de pago.',
        advancedOptions: {
          title: 'Detalles avanzados',
          disableSwaps: 'Deshabilitar intercambios',
          customerPays: 'Cliente paga vía USDC (SOL)',
          paymentSuccessUrl: 'URL de pago exitoso',
        },
      },
      subscriptionForm: {
        title: 'Crear una suscripción',
        titleEditing: 'Editar suscripción',
        tabs: {
          attachPaymentLink: 'Adjuntar a un enlace de pago',
          sendAsAnInvoice: 'Enviar como factura',
        },
      },
    },

    events: {
      [SphereEventNames.PENDING_PAYMENT]: 'Pago iniciado',
      [SphereEventNames.SUCCESSFUL_PAYMENT]: 'Pago exitoso',
      [SphereEventNames.FAILED_PAYMENT]: 'Pago fallido',
      [SphereEventNames.CANCELLED_PAYMENT]: 'Pago cancelado',
      [SphereEventNames.INCOMPLETE_PAYMENT]: 'Pago incompleto',
      [SphereEventNames.CREATE_SUBSCRIPTION]: 'Suscripción creada.',
      [SphereEventNames.UPDATE_SUBSCRIPTION]: 'Suscripción actualizada.',
      [SphereEventNames.PAST_DUE_SUBSCRIPTION]: 'Suscripción vencida.',
      [SphereEventNames.BILL_SUBSCRIPTION]: 'Suscripción facturada.',
      [SphereEventNames.APPROVE_SUBSCRIPTION]:
        'Financiación de suscripción aprobada.',
      [SphereEventNames.RETRY_BILL_SUBSCRIPTION]:
        'Reintento de facturación de suscripción.',
      [SphereEventNames.CANCEL_SUBSCRIPTION]: 'Suscripción cancelada.',
      [SphereEventNames.LOW_BALANCE_DELEGATED_SUBSCRIPTION]:
        'Suscripción saldo delegado bajo.',
      [SphereEventNames.LOW_BALANCE_ESCROWED_SUBSCRIPTION]:
        'Suscripción con saldo bajo en depósito.',
      [SphereEventNames.INSUFFICIENT_DELEGATED_APPROVED_BALANCE_SUBSCRIPTION]:
        'La suscripción no tiene suficiente cantidad de delegados aprobados.',
      [SphereEventNames.INSUFFICIENT_ESCROWED_BALANCE_SUBSCRIPTION]:
        'La suscripción no tiene suficiente monto en depósito.',
      [SphereEventNames.INSUFFICIENT_DELEGATED_BALANCE_SUBSCRIPTION]:
        'La suscripción no tiene fondos suficientes en la billetera del cliente.',
      [SphereEventNames.REDELEGATED_SUBSCRIPTION]:
        'La suscripción ha vuelto a delegar su cuenta de financiación.',
    },
    badges: {
      customerKycStatus: {
        [CustomerKycStatus.AdditionalReviewRequired]:
          'Revisión Adicional Requerida',
        [CustomerKycStatus.Approved]: 'Aprobado',
        [CustomerKycStatus.Incomplete]: 'Incompleto',
        [CustomerKycStatus.Rejected]: 'Rechazado',
        [CustomerKycStatus.Pending]: 'Pendiente',
      },
      customerTosStatus: {
        [CustomerTosStatus.Approved]: 'Aprobado',
        [CustomerTosStatus.Pending]: 'Pendiente',
        [CustomerTosStatus.Incomplete]: 'Incompleto',
      },
      subscriptionStatus: {
        [SubscriptionStatus.ACTIVE]: 'Activa',
        [SubscriptionStatus.CANCELED]: 'Cancelada',
        [SubscriptionStatus.INCOMPLETE]: 'Incompleta',
        [SubscriptionStatus.PAST_DUE]: 'Vencida',
        [SubscriptionStatus.PENDING]: 'Pendiente',
      },
      paymentLinkStatus: {
        [PaymentLinkStatus.ACTIVE]: 'Activo',
        [PaymentLinkStatus.INACTIVE]: 'Fallido',
      },
      membershipRole: {
        [ROLES.Admin]: 'Administrador',
        [ROLES.ViewOnly]: 'Solo Lectura',
      },
      priceType: {
        [PriceType.ONE_TIME]: 'Pago único',
        [PriceType.RECURRING]: 'Recurrente',
      },
      requestType: {
        [RequestType.EGRESS]: 'Salida',
        [RequestType.INGRESS]: 'Entrada',
      },
      requestStatus: {
        warning: 'Advertencia',
        ok: 'Correcto',
        error: 'Error',
      },
      genericPayoutType: {
        [PayoutType.OnRamp]: 'Depósito',
        [PayoutType.OffRamp]: 'Retiro',
        [PayoutType.OnChain]: 'Transacción',
      },
      technicalPayoutType: {
        [PayoutType.OnRamp]: 'On-Ramp',
        [PayoutType.OffRamp]: 'Off-Ramp',
        [PayoutType.OnChain]: 'On-Chain',
      },
      eventName: {
        [SphereEventNames.ALL]: '*',
        [SphereEventNames.CREATE_ADDRESS]: 'Crear',
        [SphereEventNames.CREATE_ALERT]: 'Crear',
        [SphereEventNames.UPDATE_ALERT]: 'Actualizar',
        [SphereEventNames.CREATE_TAX_RATE]: 'Crear',
        [SphereEventNames.DELETE_TAX_RATE]: 'Eliminar',
        [SphereEventNames.UPDATE_TAX_RATE]: 'Actualizar',
        [SphereEventNames.CREATE_SHIPPING_RATE]: 'Crear',
        [SphereEventNames.CREATE_API_KEY]: 'Crear',
        [SphereEventNames.UPDATE_API_KEY]: 'Actualizar',
        [SphereEventNames.DELETE_API_KEY]: 'Eliminar',
        [SphereEventNames.CREATE_RESTRICTED_API_KEY]: 'Crear',
        [SphereEventNames.UPDATE_RESTRICTED_API_KEY]: 'Actualizar',
        [SphereEventNames.DELETE_RESTRICTED_API_KEY]: 'Eliminar',
        [SphereEventNames.UPDATE_APPLICATION]: 'Actualizar',
        [SphereEventNames.CREATE_CUSTOMER]: 'Crear',
        [SphereEventNames.UPDATE_CUSTOMER]: 'Actualizar',
        [SphereEventNames.DELETE_CUSTOMER]: 'Eliminar',
        [SphereEventNames.APPROVE_CUSTOMER]: 'Aprobar',
        [SphereEventNames.TOS_CUSTOMER_PENDING]: 'Pendiente',
        [SphereEventNames.TOS_CUSTOMER_SUCCESS]: 'Exitoso',
        [SphereEventNames.KYC_CUSTOMER_PENDING]: 'Pendiente',
        [SphereEventNames.KYC_CUSTOMER_SUCCESS]: 'Exitoso',
        [SphereEventNames.KYC_ADDITIONAL_REVIEW_REQUIRED]:
          'Información adicional de KYC requerida',
        [SphereEventNames.KYC_CUSTOMER_REJECTED]: 'Fallido',
        [SphereEventNames.CREATE_COUPON]: 'Crear',
        [SphereEventNames.UPDATE_COUPON]: 'Actualizar',
        [SphereEventNames.DELETE_COUPON]: 'Eliminar',
        [SphereEventNames.CREATE_PROMOTION_CODE]: 'Crear',
        [SphereEventNames.UPDATE_PROMOTION_CODE]: 'Actualizar',
        [SphereEventNames.DELETE_PROMOTION_CODE]: 'Actualizar',
        [SphereEventNames.CREATE_NFT_DISCOUNT]: 'Crear',
        [SphereEventNames.UPDATE_NFT_DISCOUNT]: 'Actualizar',
        [SphereEventNames.DELETE_NFT_DISCOUNT]: 'Eliminar',
        [SphereEventNames.CREATE_EMAIL]: 'Crear',
        [SphereEventNames.CREATE_FILE]: 'Crear',
        [SphereEventNames.PENDING_PAYMENT]: 'Pendiente',
        [SphereEventNames.CANCELLED_PAYMENT]: 'Cancelado',
        [SphereEventNames.SUCCESSFUL_PAYMENT]: 'Exitoso',
        [SphereEventNames.INCOMPLETE_PAYMENT]: 'Actualizar',
        [SphereEventNames.FAILED_PAYMENT]: 'Fallido',
        [SphereEventNames.REFUNDED_CARD_PAYMENT]: 'Actualizar',
        [SphereEventNames.CREATE_PAYMENT_LINK]: 'Crear',
        [SphereEventNames.DELETE_PAYMENT_LINK]: 'Eliminar',
        [SphereEventNames.UPDATE_PAYMENT_LINK]: 'Actualizar',
        [SphereEventNames.CREATE_PAYMENT_LINK_PHASE]: 'Crear',
        [SphereEventNames.UPDATE_PAYMENT_LINK_PHASE]: 'Actualizar',
        [SphereEventNames.CREATE_PAYMENT_LINK_PHASE_LIMIT]: 'Crear',
        [SphereEventNames.UPDATE_PAYMENT_LINK_PHASE_LIMIT]: 'Actualizar',
        [SphereEventNames.CREATE_PRICE]: 'Crear',
        [SphereEventNames.DELETE_PRICE]: 'Eliminar',
        [SphereEventNames.UPDATE_PRICE]: 'Actualizar',
        [SphereEventNames.CREATE_PRODUCT]: 'Crear',
        [SphereEventNames.DELETE_PRODUCT]: 'Eliminar',
        [SphereEventNames.UPDATE_PRODUCT]: 'Actualizar',
        [SphereEventNames.PENDING_REFUND]: 'Pendiente',
        [SphereEventNames.SUCCESSFUL_REFUND]: 'Exitoso',
        [SphereEventNames.INCOMPLETE_REFUND]: 'Actualizar',
        [SphereEventNames.FAILED_REFUND]: 'Fallido',
        [SphereEventNames.UPDATE_REFUND]: 'Actualizar',
        [SphereEventNames.CREATE_TRANSACTION]: 'Crear',
        [SphereEventNames.UPDATE_TRANSACTION]: 'Actualizar',
        [SphereEventNames.CREATE_SUBSCRIPTION]: 'Crear',
        [SphereEventNames.UPDATE_SUBSCRIPTION]: 'Actualizar',
        [SphereEventNames.PAST_DUE_SUBSCRIPTION]: 'Vencida',
        [SphereEventNames.CANCEL_SUBSCRIPTION]: 'Cancelada',
        [SphereEventNames.BILL_SUBSCRIPTION]: 'Facturada',
        [SphereEventNames.RETRY_BILL_SUBSCRIPTION]: 'Reintento de facturación',
        [SphereEventNames.APPROVE_SUBSCRIPTION]: 'Aprobar',
        [SphereEventNames.LOW_BALANCE_ESCROWED_SUBSCRIPTION]: 'Saldo bajo',
        [SphereEventNames.LOW_BALANCE_DELEGATED_SUBSCRIPTION]: 'Saldo bajo',
        [SphereEventNames.INSUFFICIENT_DELEGATED_BALANCE_SUBSCRIPTION]:
          'Saldo insuficiente',
        [SphereEventNames.INSUFFICIENT_DELEGATED_APPROVED_BALANCE_SUBSCRIPTION]:
          'Saldo insuficiente',
        [SphereEventNames.INSUFFICIENT_ESCROWED_BALANCE_SUBSCRIPTION]:
          'Saldo insuficiente',
        [SphereEventNames.REDELEGATED_SUBSCRIPTION]: 'Cliente redelegado',
        [SphereEventNames.CREATE_USAGE_RECORD]: 'Crear',
        [SphereEventNames.CREATE_BANK_ACCOUNT]: 'Crear',
        [SphereEventNames.UPDATE_BANK_ACCOUNT]: 'Actualizar',
        [SphereEventNames.CREATE_PAYOUT]: 'Crear',
        [SphereEventNames.PENDING_PAYOUT]: 'Pendiente',
        [SphereEventNames.FUNDED_PAYOUT]: 'Financiado',
        [SphereEventNames.DISPATCHED_PAYOUT]: 'Enviado',
        [SphereEventNames.PROCESSING_PAYOUT]: 'Procesando',
        [SphereEventNames.SUCCESSFUL_PAYOUT]: 'Exitoso',
        [SphereEventNames.CANCELLED_PAYOUT]: 'Cancelado',
        [SphereEventNames.FAILED_PAYOUT]: 'Fallido',
        [SphereEventNames.REFUNDED_PAYOUT]: 'Reembolsado',
        [SphereEventNames.CREATE_WEBHOOK]: 'Crear',
        [SphereEventNames.UPDATE_WEBHOOK]: 'Actualizar',
        [SphereEventNames.DELETE_WEBHOOK]: 'Eliminar',
        [SphereEventNames.CREATE_WALLET]: 'Crear',
        [SphereEventNames.CUSTODIAL_ACTIVATION_SUCCESSFUL]: 'Exitoso',
      },
      paymentType: {
        [PaymentType.PAYMENT_LINK]: 'Enlace de Pago',
        [PaymentType.SUBSCRIPTION]: 'Suscripción',
      },
      payoutStatus: {
        [PayoutStatus.CANCELLED]: 'Cancelado',
        [PayoutStatus.REFUNDED]: 'Reembolsado',
        [PayoutStatus.FAILED]: 'Fallido',
        [PayoutStatus.FAILED_PRECONDITION]: 'Fallido',
        [PayoutStatus.FUNDED]: 'Financiado',
        [PayoutStatus.PENDING]: 'Pendiente',
        [PayoutStatus.PROCESSING]: 'Procesando',
        [PayoutStatus.SUCCEEDED]: 'Exitoso',
      },
      paymentStatus: {
        [PaymentStatus.CONFIRMING]: 'Confirmando',
        [PaymentStatus.FAILED]: 'Fallido',
        [PaymentStatus.PENDING]: 'Pendiente',
        [PaymentStatus.REFUNDED]: 'Reembolsado',
        [PaymentStatus.SUCCEEDED]: 'Exitoso',
      },
      bankAccountStatus: {
        [BankAccountStatus.Active]: 'Activa',
        [BankAccountStatus.Inactive]: 'Eliminada',
        [BankAccountStatus.Invalid]: 'Inválida',
        [BankAccountStatus.Pending]: 'Pendiente',
      },
      cardType: {
        [AstraFiCard.Type.CREDIT]: 'Crédito',
        [AstraFiCard.Type.DEBIT]: 'Débito',
      },
      cardStatus: {
        [AstraFiCard.Status.APPROVED]: 'Aprobada',
        [AstraFiCard.Status.FLAGGED]: 'Marcada',
        [AstraFiCard.Status.PENDING]: 'Pendiente',
        [AstraFiCard.Status.REJECTED]: 'Rechazada',
        [AstraFiCard.Status.REMOVED]: 'Eliminada',
      },
      webhookActiveStatus: {
        active: 'Activo',
        inactive: 'Inactivo',
      },
    },
  },
  checkout: {
    paymentDetails: {
      summary: {
        heading: 'Resumen de artículo',
        items_one: '{{count}} Artículo',
        items_other: '{{count}} Artículos',
        taxes: 'Impuesto',
        subtotal: 'Subtotal',
        walletApproval: 'Aprobación de billetera',
        escrow: 'Depósito en garantía',
        escrowPeriodInfo:
          'Se realiza un cobro cada {{interval}} desde un depósito en garantía ya financiado. Puedes cancelar o retirar tus fondos cuando quieras.',
        delegatedPeriodInfo:
          'Autoriza pagos futuros directamente desde tu billetera durante un período de hasta {{defaultLength}} {{timeUnit}}. Puedes retirar fondos o cancelar en cualquier momento.',
        totalDueToday: 'Total a pagar hoy',
        havingTroubleWithPayment: '¿Problemas al realizar tu pago?',
        reportItToUs: 'Reporta tu problema',
        privacyPolicy: 'Política de privacidad',
        termsOfService: 'Términos de Servicio',
      },
      form: {
        labels: {
          name: 'Nombre',
          email: 'Correo electrónico',
          shippingAddress: 'Dirección de envío',
          network: 'Red',
          token: 'Token',
          upTo: 'Hasta',
          totalDueToday: 'Total a pagar hoy',
          walletApproval: 'Aprobación de billetera',
          escrowAmount: 'Monto en garantía',
          useShippingAddressAsBillingAddress:
            'Usar la dirección de envío como dirección de facturación',
        },
        placeholders: {
          firstName: 'Primer',
          lastName: 'Apellido',
          email: 'Ingresar correo electrónico',
          addressLine1: 'Línea de Dirección 1',
          addressLine2: 'Línea de Dirección 2 (Opcional)',
          city: 'Ciudad',
          postalCode: 'Código postal',
          state: 'Estado o Provincia',
          country: 'País',
          cardName: 'Nombre en Tarjeta',
        },
        info: {
          resumeSubscription: 'Reanudar suscripción',
          resumeSubscriptionDescription:
            "Hemos detectado una suscripción previa a este producto. Al hacer clic en 'Reanudar suscripción', la suscripción se reactivará y se te cobrará el mismo monto que antes.",
          poweredBy: 'Desarrollado/Impulsado por',
          disclaimer:
            'Al realizar esta compra, aceptas los términos y condiciones de Sphere y autorizas el cargo a tu billetera tanto por este pago como por futuros pagos  correspondientes a suscripciones adquiridas.',
        },
        heading: {
          payment: 'Pago',
          paymentInformation: 'Información de pago',
          payWith: 'Pagar con',
          confirmation: 'Confirmación',
        },
        cta: {
          soldOut: 'Agotado',
          connectWallet: 'Conectar billetera',
          insufficientFunds: 'Fondos Insuficientes',
          invalidPaymentAmount: 'Monto de pago inválido',
          resumeSubscription: 'Reanudar suscripción',
          subscribeFor: 'Suscribirse por',
          pay: 'Pagar',
          payWithQR: 'Pague con código QR',
        },
        errors: {
          insufficientFunds: {
            header: 'Fondos Insuficientes',
            sol: 'No tienes fondos suficientes para completar esta compra. Por favor, transfiere más fondos a tu billetera, elige un token diferente o usa una billetera con fondos suficientes.',
            notSol:
              'No tienes fondos suficientes para completar esta compra. Por favor, transfiere más fondos a tu billetera, o usa una billetera con fondos suficientes.',
          },
          priceImpact: {
            header: 'Aumento de precio de {{priceImpactPct}}%',
            description:
              'Pagar con {{selectedToken}} ha aumentado tu precio en {{priceImpactPct}}%. Para evitar pagar a una tasa desfavorable, puedes cambiar tu método de pago.',
          },
          noQuotes: {
            header: 'El monto de la compra es demasiado alto',
            description:
              'Por favor, elige un token diferente o compra menos unidades.',
          },
        },
      },
    },
    intervalDescriptions: {
      oneTime: 'Compra única',
      year: 'año',
      month: 'mes',
      week: 'semana',
      day: 'día',
      minute: 'minuto',
      yearly: 'anual',
      monthly: 'mensual',
      weekly: 'semanal',
      daily: 'diario',
      everyInterval: 'cada',
      twicePerInterval: 'dos veces por {{intervalUnit}}',
      generalCase: 'veces por',
      billed: 'Facturado',
    },
  },
  // ramp-widget translations
  translations: {
    validation: {
      required: 'Requerido',
      email: 'Correo electrónico inválido',
      passwordsMatch: 'Las contraseñas no coinciden',
      invalidCountry: 'País inválido',
      invalidState: 'Estado inválido',
      postalCode: 'Código postal inválido',
      invalidIban: 'IBAN inválido',
      invalidWalletAddress: 'Dirección de billetera inválida',
      positive: 'Ingresa un número positivo',
      maxLength: {
        wireMessage:
          'Mensaje de transferencia debe tener menos de 105 caracteres',
        accountHolderName:
          'El nombre del titular de la cuenta debe tener menos de 35 caracteres',
        addressLine1:
          'La línea de dirección 1 debe tener menos de 35 caracteres',
        addressLine2:
          'La línea de dirección 2 debe tener menos de 35 caracteres',
        city: 'La ciudad debe tener menos de 35 caracteres',
      },
      invalidSortCode:
        'El código de ruta debe estar en el formato XX-XX-XX o XXXXXX',
      invalidAccountNumber: 'Número de cuenta inválido',
      phoneNumberInvalid:
        'Por favor, introduce un número de teléfono válido en formato internacional (ej., +1234567890)',
    },
    sidebar: {
      createPayout: 'Crear transferencia',
      withdrawToBank: 'Retirar para el banco',
      depositToWallet: 'Depositar en la billetera',
      myAccounts: 'Mis cuentas',
      accounts: 'Cuentas',
      payoutHistory: 'Mis transferencias',
      history: 'Transferencias',
      settings: 'Configuración',
      offRamp: 'Retirar al banco',
      onRamp: 'Depositar en billetera',
    },
    signIn: {
      title: 'Bienvenido',
      description: 'Inicia sesión en tu cuenta',
      form: {
        email: 'Correo electrónico',
        emailPlaceholder: 'Ingresa tu correo electrónico',
        password: 'Contraseña',
        cta: 'Iniciar sesión',
      },
      or: 'o',
      signInWith: 'Iniciar sesión con {{provider}}',
      footer: {
        text: '¿No tienes una cuenta?',
        cta: 'Crear una',
      },
      error: {
        title: 'Algo salió mal',
        text: 'No se encontró un usuario con esa combinación de correo electrónico y contraseña. Si el problema persiste, por favor contacta al soporte.',
      },
      operatingResidency: {
        title: '¡Casi listo!',
        description:
          'Necesitamos algunos detalles adicionales antes de conectarte...',
        form: {
          label: 'Residencia operativa',
          countryPlaceholder: 'Selecciona un país',
        },
        error: {
          title: 'Región no admitida actualmente',
          text: 'Gracias por su interés en utilizar {{merchantName}}. Lamentablemente, debido a restricciones regulatorias, no podemos registrar clientes de su país/estado en este momento. Entendemos que esto puede ser decepcionante y pedimos disculpas por cualquier inconveniente que esto pueda causar.<br /><br />Si hay algún cambio en estas regulaciones en el futuro, estaremos encantados de reconsiderar su solicitud. Gracias por su comprensión.',
        },
        info: {
          title: 'Importante',
          text: 'Se requiere una cuenta bancaria de EE. UU. o UE para procesar transacciones, independientemente de donde residas o operes tu negocio.',
        },
      },
      otp: {
        title: 'Verifica tu correo electrónico',
        description: {
          line1: 'Enviamos un enlace de verificación a {{email}}.',
          line2: 'Revisa tu bandeja de entrada para los siguientes pasos.',
        },
        cta: 'Continuar',
        footer: {
          text: '¿No recibiste el correo?',
          cta: 'Reenviarlo',
        },
        error: {
          invalid: {
            title: 'Código inválido',
            text: 'El código que ingresaste ya no es válido. Por favor solicita un nuevo codigo.',
            buttons: {
              backToSignIn: 'Volver a iniciar sesión',
              resend: 'Reenviar',
            },
          },
          mismatch: {
            title: 'Código incorrecto',
            text: 'El código que ingresaste es incorrecto. Por favor, inténtalo de nuevo.',
          },
        },
      },
    },
    signUp: {
      title: 'Has sido invitado',
      description:
        'Confirmamos tu correo electrónico, ahora crea una contraseña para completar la configuración de tu cuenta.',
      form: {
        email: 'Correo electrónico',
        createAPassword: 'Crear una contraseña',
        enterPassword: 'Ingresa la contraseña',
        confirmPassword: 'Confirmar contraseña',
        cta: 'Crear cuenta',
      },
    },
    businessCategory: {
      title: 'Categoría de negocio',
      description:
        'Por favor, selecciona la categoría de negocio que mejor describe tu negocio.',
      form: {
        label: '¿Tu negocio se identifica como alguna de las siguientes?',
        placeholder: 'Selecciona una categoría de negocio',
      },
      info: {
        title: 'Por favor, confirma',
        description:
          'Debido a la política de la empresa, algunos negocios pueden requerir información adicional o no ser compatibles. Por favor, verifica si tu empresa se identifica como alguna de las siguientes.',
      },
      error: {
        title: 'Negocio no compatible actualmente',
        text: 'Gracias por tu interés en usar Sphere. Desafortunadamente, debido a restricciones regulatorias, no podemos registrar clientes de su país/estado en este momento. Entendemos que esto puede ser decepcionante y pedimos disculpas por cualquier inconveniente que esto pueda causar.<br /><br />Si hay algún cambio en estas regulaciones en el futuro, estaremos encantados de reconsiderar su solicitud. Gracias por su comprensión.',
      },
    },
    onboarding: {
      title: 'Comenzar',
      description:
        'Comienza a transferir entre USD y cripto en unos pocos pasos simples.',
      steps: {
        tos: {
          title: 'Términos de servicio',
          description:
            'Esta aplicación utiliza Sphere y otros terceros para mover fondos de manera segura.',
        },
        msa: {
          title: 'Firmar MSA',
          description:
            'Revise y firme el Acuerdo Maestro de Servicio (MSA) a través del correo electrónico de DocuSign.',
          tooltipWhenDisabled: 'Primero debes aceptar los Términos de Servicio',
        },
        connectBank: {
          title: 'Conectar banco',
          description: 'Verifica tu banco manualmente.',
          tooltipWhenDisabled:
            'Finaliza las etapas anteriores para conectar tu banco',
        },
        kyc: {
          title: 'Verificar identidad',
          description:
            'La verificación KYC es el proceso obligatorio de identificación y verificación de identidad.',
          tooltipWhenDisabled: 'Primero debes aceptar los Términos de Servicio',
          customerType: {
            individual: {
              title: 'Verificar como individuo',
              description:
                'Los tiempos de identificación del cliente promedian entre 1 y 30 minutos.',
            },
            business: {
              title: 'Verificar una empresa',
              description:
                'Los tiempos de verificación comercial promedian entre 3 y 5 días hábiles.',
            },
            continue: 'Continuar',
          },
          alert: {
            description:
              'El siguiente paso es la verificación. La verificación puede tardar hasta 3 días en los casos que requieren revisión manual, pero generalmente es más rápida. Después de enviar su información, le informaremos cuándo puede regresar para tomar el siguiente paso.',
          },
          submitted: {
            title: '¡KYC enviado!',
            description: `Se ha enviado su verificación KYC. Recibirá un correo electrónico una vez aprobada. La mayoría de las verificaciones se procesan en un plazo de 1 a 30 minutos, pero puede tardar hasta 3 días si se requiere una revisión manual. Si experimenta retrasos después de este plazo, póngase en contacto con el equipo de soporte.`,
          },
          openLinkInNewTab:
            '¿Tiene problemas para acceder a la cámara? Haga clic aquí para abrir el formulario en una nueva pestaña.',
        },
        msaEmail: {
          title: 'Verifica tu correo electrónico',
          description:
            'Hemos enviado un enlace de verificación de MSA a {{email}}. Una vez que firmes el documento, tu estado se actualizará y podrás proceder al siguiente paso del proceso de incorporación.',
          cta: 'Firmar MSA',
          resendEmailCta: 'Reenviar Correo Electrónico',
          emailSent: '¡Enviado!',
          pendingSignatureBadge: 'Firma pendiente',
        },
      },
      badge: {
        pending: 'Pendiente',
        complete: 'Completo',
        underReview: 'En revisión',
        softRejected: 'Acción requerida',
        rejected: 'Rechazado',
        error: 'Error',
        tooltips: {
          underReviewBySumsub:
            'Estamos revisando su envío a través de nuestro proveedor externo. Esto generalmente toma una hora, pero a veces puede demorar hasta 3 días para casos que requieren revisión manual. Si experimenta retrasos prolongados más allá de esto, comuníquese con el soporte.',
          underReviewByBridge:
            'Ha sido verificado con éxito por nuestro proveedor externo. Por favor, espere mientras finalizamos el proceso. Vuelva a verificar más tarde o espere hasta que reciba un correo electrónico sobre su estado dentro de 1-3 días hábiles. Si experimenta retrasos prolongados más allá de esto, comuníquese con el soporte.',
        },
      },
      cta: {
        start: 'Empezar',
        continue: 'Continuar',
        incomplete: 'Comenzar',
        pending: 'En revisión',
        softRejected: 'Corregir',
        rejected: 'Contactar soporte',
        error: 'Reintentar',
        getStarted: 'Comenzar',
        next: 'Continuar',
        checkStatus: 'Revisar estado',
        tooltips: {
          contactSupport:
            'Desafortunadamente, no pudimos finalizar tu verificación con nuestros sistemas internos. Si crees que esto es un error, por favor contacta a soporte para asistencia.',
        },
      },
      failedToVerify: {
        title: 'No se pudo verificar tu cuenta',
        description:
          'Gracias por tu interés en usar RAMP. Desafortunadamente, debido a nuestras políticas de cumplimiento, no podemos proceder con el registro de tu cuenta en este momento. Entendemos que esto puede ser molesto y disculpamos por cualquier inconveniente que esto pueda causar. Si crees que esta decisión fue un error, por favor contáctanos a nuestro equipo en <0>compliance@spherepay.co</0>.',
        cta: 'Cerrar sesión',
        badge: 'Falló',
      },
    },
    myAccounts: {
      title: 'Mis Cuentas',
      bank: {
        tabTitle: 'Mis Cuentas Bancarias',
        action: 'Conectar cuenta bancaria',
        empty: {
          title: 'No hay cuentas bancarias',
          description: 'Conecta tus cuentas bancarias para comenzar.',
          action: 'Conectar banco',
        },
        table: {
          header: {
            bankName: 'Banco',
            bankNameAccountType: {
              checking: 'Corriente',
              savings: 'Ahorros',
              instapay: 'Instapay',
              pesonet: 'Pesonet',
            },
            last4: 'Últimos 4',
            routingNumber: 'Nº de ruta',
            status: 'Estado',
            linkedWallets: 'Carteras Vinculadas',
            actions: 'Acciones',
          },
          actions: {
            delete: {
              label: 'Eliminar',
              dialog: {
                title: 'Eliminar cuenta bancaria',
                description: '¿Seguro que deseas eliminar esta cuenta?',
              },
              cta: 'Eliminar cuenta',
            },
          },
          tooltips: {
            deletedAt: 'Has eliminado esta cuenta {{date}}',
          },
        },
        form: {
          title: 'Adcicionar Nueva Cuenta Bancaria',
          plaidAlert: {
            title: 'Atención',
            description:
              'Si planeas iniciar transferencias entre tarjetas y cuentas bancarias, <i>o</i> crear transferencias de on-ramp automáticas, tu banco <strong>debe</strong> estar conectado a través de Plaid.',
          },
          plaidButton: 'Conectar con Plaid',
          accountOwner: 'Propietario de la cuenta',
          accountOwnerType: {
            personal: 'Personal',
            third_party: 'Terceros',
          },
          bankType: 'Tipo de banco',
          currency: 'Moneda',
          bankName: 'Nombre del banco',
          bankNamePlaceholder: 'Comenzar a escribir...',
          bankNamePlaceholderNoCustomName: 'Seleccione banco...',
          noBankFound: 'Presiona Enter para seleccionar {{name}}',
          accountType: 'Tipo de cuenta',
          accountHolder: 'Titular de la cuenta',
          accountHolderName: 'Nombre del titular de la cuenta',
          accountHolderFirstName: 'Nombre',
          accountHolderLastName: 'Apellido',
          accountHolderBirthDate: 'Fecha de nacimiento',
          business: 'Empresa',
          businessName: 'Nombre',
          businessDBAName: 'Nombre comercial (si aplica)',
          businessType: 'Tipo',
          businessTaxId: 'Número de identificación fiscal',
          businessWebsite: 'Sitio web',
          businessRegistrationNumber: 'Número de registro',
          businessCardType: {
            soleProprietorship: 'Empresario Individual',
            partnership: 'Sociedad',
            llc: 'Sociedad Limitada',
            corporation: 'Corporación',
            cooperative: 'Cooperativa',
            trust: 'Fideicomiso',
          },
          phone: 'Teléfono',
          email: 'Correo electrónico',
          routingNumber: 'Número de ruta',
          accountNumber: 'Número de cuenta',
          bic: 'Código BIC',
          iban: 'IBAN',
          clabe: 'Número CLABE',
          pixKey: 'Clave PIX',
          taxID: 'ID Fiscal',
          channelSubject: 'Categoría de Pago',
          ifsc: 'Código IFSC',
          bankID: 'Código del Banco',
          bankDocumentNumber: 'Número de documento del banco',
          sortCode: 'Código de orden',
          branchCode: 'Código de sucursal',
          branchCodeHelpText:
            'Formato combinado de Número de Institución (3 dígitos) y Número de Tránsito (5 dígitos).',
          nickname: 'Apodo',
          nicknameDescription: 'Opcional.',
          beneficiaryAddress: {
            main: 'Dirección del beneficiario',
            country: 'País',
            countryPlaceholder: 'Selecciona un país',
            addressLine1: 'Línea de dirección 1',
            addressLine2: 'Línea de dirección 2',
            city: 'Ciudad',
            state: 'Estado',
            statePlaceholders: {
              default: 'Selecciona un estado',
              noCountrySelected: 'Selecciona un país primero',
              empty: 'No se encontraron estados',
            },
            postalCode: 'Código postal',
          },
          cta: 'Agregar cuenta',
          success: {
            title: 'Cuenta bancaria agregada',
            description: 'Tu cuenta bancaria ha sido agregada.',
          },
          error: {
            title: 'Algo salió mal',
            description:
              'No pudimos agregar tu cuenta bancaria en este momento. ({{error}})',
          },
          verificationPending: {
            title: 'Verificación Pendiente',
            description:
              'Estamos verificando tu cuenta. Este proceso puede durar hasta 24 horas. Te notificaremos una vez que la verificación esté completa.',
            cta: 'Contactar Soporte',
          },
        },
        offloaderWallets: {
          form: {
            networkDropdown: {
              label: 'Seleccione una red:',
              placeholder: 'Seleccione una red:',
            },
            stablecoinDropdown: {
              label: 'Seleccione una ficha:',
              placeholder: 'Seleccione una ficha:',
            },
            transferMethodDropdown: {
              label: 'Seleccione un método de transferencia:',
              placeholder: 'Seleccione un método de transferencia:',
            },
          },
          table: {
            actions: {
              generateWalletAddress: 'Generar dirección de billetera',
            },
            cell: {
              viewWallets: 'Ver carteras',
            },
          },
          modal: {
            toast: {
              success: 'La billetera del descargador se creó correctamente',
              error:
                'Algo salió mal con la creación de la billetera del descargador',
            },
            title: 'Generar dirección de billetera',
            description:
              'Selecciona la blockchain para generar tu nueva dirección de billetera. Esta dirección puede usarse para recibir y enviar fondos con otras direcciones en la misma red.',
            button: {
              confirm: 'Confirmar',
              cancel: 'Cancelar',
              close: 'Cerca',
            },
            linkedWallets: {
              title: 'Carteras vinculadas',
              description:
                'A continuación se muestran las billeteras asociadas a su cuenta bancaria. Puede usar estas direcciones para recibir fondos en las respectivas cadenas de bloques.',
              addressHeader: 'Direcciones de salida:',
            },
          },
        },
      },
      wallet: {
        tabTitle: 'Billeteras',
        actions: {
          primary: 'Conectar billetera',
          secondary: 'Entra manualmente',
        },
        empty: {
          title: 'No hay billeteras',
          description: 'Conecta tus billeteras para comenzar.',
          action: 'Conectar billetera',
        },
        connection: {
          selector: {
            trigger: 'Conectar billetera',
            title: 'Conecta una billetera Solana para continuar',
            installedDescription: 'Detectada',
            moreOptions: 'Más opciones',
          },
        },
        form: {
          title: 'Agregar Billetera {{networkName}}',
          description: {
            line1:
              'Enviar cripto por la red equivocada podría significar la pérdida de tus fondos.',
            line2:
              'Asegúrate de elegir la red correcta. En caso de duda, envía una pequeña cantidad.',
          },
          nickname: 'Apodo',
          nicknamePlaceholder: 'Ej., Billetera Sphere',
          nicknameDescription: 'Máximo 20 caracteres.',
          address: 'Dirección',
          consent: {
            label: 'Confirmo que la información anterior es correcta.',
            description:
              'Enviar cripto por la red equivocada podría significar la pérdida de tus fondos.',
          },
          cta: 'Agregar billetera',
          success: {
            title: 'Billetera agregada',
            description: 'Tu billetera ha sido agregada.',
          },
          error: {
            title: 'Algo salió mal',
            description:
              'No pudimos agregar tu billetera en este momento. ({{error}})',
          },
        },
        editForm: {
          title: 'Editar Billetera',
          cta: 'Guardar',
          success: {
            title: 'Billetera actualizada',
            description: 'Tu billetera ha sido actualizada.',
          },
          error: {
            title: 'Algo salió mal',
            description:
              'No pudimos actualizar tu billetera en este momento. ({{error}})',
          },
          deleteCta: 'Eliminar',
          deleteDialog: {
            title: 'Eliminar billetera',
            description: '¿Seguro que deseas eliminar esta billetera?',
          },
          deleteSuccess: {
            title: 'Billetera eliminada',
            description: 'Tu billetera ha sido eliminada.',
          },
          deleteError: {
            title: 'Algo salió mal',
            description:
              'No pudimos eliminar tu billetera en este momento. ({{error}})',
          },
        },
        items: {
          actions: {
            edit: 'Editar',
            copyAddress: 'Copiar dirección',
            copy: 'Copiar',
          },
        },
      },
      card: {
        tabTitle: 'Tarjetas',
        action: 'Conectar tarjeta',
        empty: {
          title: 'No hay tarjetas',
          description: 'Conecta tus tarjetas para comenzar.',
          action: 'Conectar tarjeta',
        },
      },
      thirdParty: {
        tabTitle: 'Cuentas terceras',
        action: 'Conectar cuenta tercera',
        table: {
          header: {
            currency: 'Moneda',
          },
          actions: {
            delete: {
              label: 'Eliminar',
              dialog: {
                title: 'Eliminar cuenta bancaria',
                description:
                  '¿Estás seguro de querer eliminar esta cuenta bancaria? Cualquier transferencia pendiente que involucre esta cuenta bancaria no se procesará incluso si ya has enviado fondos.',
              },
              cta: 'Eliminar cuenta bancaria',
            },
            details: {
              label: 'Ver detalles',
              dialog: {
                title: 'Detalles de la cuenta bancaria',
                description:
                  'Ver los detalles de la cuenta bancaria de terceros.',
              },
              cta: 'Ver detalles',
            },
          },
        },
        empty: {
          title: 'No hay cuentas terceras',
          description: 'Conecta tus cuentas terceras para comenzar.',
          action: 'Conectar cuenta tercera',
        },
      },
    },
    createPayout: {
      title: 'Crear Transferencia',
      description: 'Selecciona tu método de transferencia a continuación.',
      onRamp: {
        card: {
          title: 'Depositar en billetera',
          description: 'Convierte efectivo en monedas digitales.',
        },
        title: 'On-ramp',
        currencies: {
          title: 'Selecciona una moneda',
          placeholder: 'Buscar...',
          options: {
            usd: {
              label: 'USD',
              description: 'Dólar estadounidense',
            },
            eur: {
              label: 'EUR',
              description: 'Euro',
            },
            brl: {
              label: 'BRL',
              description: 'Real brasileño',
            },
            mxn: {
              label: 'MXN',
              description: 'Peso mexicano',
            },
            php: {
              label: 'PHP',
              description: 'Peso filipino',
            },
            inr: {
              label: 'INR',
              description: 'Rupia india',
            },
          },
          verificationPending: {
            title: 'Verificación Pendiente',
            description:
              'Estamos verificando tu cuenta. Por lo tanto, no tienes acceso a ninguna de nuestras monedas soportadas en este momento.',
            cta: 'Contactar Soporte',
          },
        },
        methods: {
          title: 'Selecciona un método de transferencia',
          options: {
            usd: {
              ach: {
                title: 'ACH',
                subtitle: 'Transferencia desde cuenta bancaria en EE.UU.',
                description:
                  'On-ramp dólares desde una cuenta bancaria estadounidense.',
                badges: {
                  recommended: 'Recomendado',
                  domestic: 'Nacional',
                  businessDays01: '0-1 Días',
                  noFees: '0 Comisiones',
                },
              },
              swift: {
                title: 'SWIFT',
                subtitle: 'Transferencia internacional a cuenta en USD',
                description: 'Ideal para transferencias internacionales.',
                badges: {
                  comingSoon: 'Próximamente',
                  international: 'Internacional',
                  businessDays05: '0-5 Días',
                },
              },
              wire: {
                title: 'Wire',
                subtitle: 'Transferencia a cuenta en USD',
                description:
                  'Adecuado para transferencias grandes o internacionales.',
                badges: {
                  international: 'Internacional',
                  businessDays02: '0-2 Días',
                  domestic: 'Nacional',
                  businessDays01: '0-1 Días',
                },
              },
            },
            eur: {
              sepa: {
                title: 'SEPA',
                subtitle: 'Transferencia desde cuenta bancaria en EUR',
                description: 'On-ramp euros desde una cuenta bancaria europea.',
                badges: {
                  recommended: 'Recomendado',
                  instant: 'Instantáneo',
                  noFees: '0 Comisiones',
                },
              },
              swift: {
                title: 'SWIFT',
                subtitle: 'Transferencia internacional a cuenta en EUR',
                description: 'Ideal para transferencias internacionales.',
                badges: {
                  comingSoon: 'Próximamente',
                  international: 'Internacional',
                  businessDays05: '0-5 Días',
                },
              },
              wire: {
                title: 'Wire',
                subtitle: 'Transferencia a cuenta en EUR',
                description:
                  'Adecuado para transferencias grandes o internacionales.',
                badges: {
                  international: 'Internacional',
                  businessDays02: '0-2 Días',
                  domestic: 'Nacional',
                  businessDays01: '0-1 Días',
                },
              },
            },
            mxn: {
              clabe: {
                title: 'CLABE',
                subtitle: 'Transferencia desde cuenta bancaria en MX',
                description:
                  'On-ramp pesos mexicanos desde una cuenta bancaria mexicana.',
                badges: {
                  recommended: 'Recomendado',
                  instant: 'Instantáneo',
                  noFees: '0 Comisiones',
                },
              },
              wire: {
                title: 'Wire',
                subtitle: 'Transferencia a cuenta en MXN',
                description:
                  'Adecuado para transferencias grandes o internacionales.',
                badges: {
                  international: 'Internacional',
                  businessDays02: '0-2 Días',
                },
              },
            },
            brl: {
              pix: {
                title: 'PIX',
                subtitle: 'Transferencia desde cuenta bancaria BR',
                description:
                  'On-ramp reales desde una cuenta bancaria brasileña.',
                badges: {
                  recommended: 'Recomendado',
                  instant: 'Instantáneo',
                  noFees: '0 Comisiones',
                },
              },
              wire: {
                title: 'Wire',
                subtitle: 'Transferencia a cuenta en BRL',
                description:
                  'Adecuado para transferencias grandes o internacionales.',
                badges: {
                  international: 'Internacional',
                  businessDays02: '0-2 Días',
                },
              },
            },
            php: {
              instapay: {
                title: 'Instapay',
                subtitle: 'Transferencia desde cuenta bancaria PH',
                description:
                  'Convierte monedas digitales y recibe PHP en una cuenta bancaria.',
                badges: {
                  amountsSmallerThan500000: 'Montos menores a 500,000 PHP',
                },
              },
              swiftpay_pesonet: {
                title: 'Swiftpay PesoNet',
                subtitle: 'Transferencia a cuenta bancaria PH',
                description:
                  'Adecuado para transferencias grandes o internacionales.',
                badges: {
                  amountsBiggerOrEqualThan500000:
                    'Montos mayores o iguales a 500,000 PHP',
                },
              },
            },
            inr: {
              neft: {
                title: 'NEFT',
                subtitle: 'Transferencia desde cuenta bancaria IN',
                description:
                  'Convierte monedas digitales y recibe INR en una cuenta bancaria.',
                badges: {
                  recommended: 'Recomendado',
                  instant: 'Instantáneo',
                  noFees: 'Sin comisiones',
                },
              },
              wire: {
                title: 'Wire',
                subtitle: 'Transferencia a cuenta bancaria INR',
                description:
                  'Adecuado para transferencias grandes o internacionales.',
                badges: {
                  international: 'Internacional',
                  businessDays02: '0-2 Días',
                },
              },
            },
          },
        },
        manualTransferAlert: {
          title: 'El proceso de on-ramp en Sphere es manual.',
          description:
            'Para agregar fondos, tendrás que transferir dinero desde tu cuenta bancaria a Sphere usando los datos que te mostraremos en el siguiente paso. Te notificaremos por correo electrónico una vez que se complete la transferencia.',
        },
        blocked: {
          title: 'On-ramping bloqueado',
          description:
            'No tienes acceso para on-ramp ninguna moneda. Por favor, contacta al soporte si necesitas ayuda.',
          cta: 'Contactar Soporte',
        },
      },
      pushToCard: {
        card: {
          title: 'On-ramp a una tarjeta de débito',
          description: 'Envía efectivo desde tu banco a tu tarjeta de débito.',
        },
      },
      offRamp: {
        title: 'Off-ramp',
        card: {
          title: 'Retirar al banco',
          description: 'Convierte monedas digitales a una cuenta bancaria.',
        },
        currencies: {
          title: 'Selecciona una moneda',
          placeholder: 'Buscar...',
          options: {
            usd: {
              label: 'USD',
              description: 'Dólar estadounidense',
            },
            eur: {
              label: 'EUR',
              description: 'Euro',
            },
            mxn: {
              label: 'MXN',
              description: 'Peso mexicano',
            },
            brl: {
              label: 'BRL',
              description: 'Real brasileño',
            },
            php: {
              label: 'PHP',
              description: 'Peso filipino',
            },
            inr: {
              label: 'INR',
              description: 'Rupia indiana',
            },
            gbp: {
              label: 'GBP',
              description: 'Libra esterlina',
            },
            cad: {
              label: 'CAD',
              description: 'Dólar canadiense',
            },
            cop: {
              label: 'COP',
              description: 'Peso colombiano',
            },
            thb: {
              label: 'THB',
              description: 'Baht tailandés',
            },
            vnd: {
              label: 'VND',
              description: 'Dong vietnamita',
            },
            sgd: {
              label: 'SGD',
              description: 'Dólar de Singapur',
            },
            idr: {
              label: 'IDR',
              description: 'Rupia indonesia',
            },
          },
          verificationPending: {
            title: 'Verificación Pendiente',
            description:
              'Estamos verificando tu cuenta. Por lo tanto, no tienes acceso a ninguna de nuestras monedas soportadas en este momento.',
            cta: 'Contactar Soporte',
          },
        },
        methods: {
          title: 'Selecciona un método de retiro',
          options: {
            usd: {
              ach: {
                title: 'ACH',
                subtitle: 'Transferencia a cuenta bancaria de EE. UU.',
                description:
                  'Más adecuado para pagos cotidianos o transferencias recurrentes dentro de EE. UU.',
                badges: {
                  recommended: 'Recomendado',
                  domestic: 'Nacional',
                  businessDays01: '0-1 días hábiles',
                  noFees: 'Sin comisiones',
                },
              },
              wire: {
                title: 'WIRE',
                subtitle: 'Transferencia a cuenta bancaria de EE. UU.',
                description:
                  'Adecuado para transferencias grandes o internacionales.',
                badges: {
                  international: 'Internacional',
                  businessDays02: '0-2 días hábiles',
                  domestic: 'Nacional',
                  businessDays01: '0-1 días hábiles',
                },
              },
              swift: {
                title: 'Swift',
                subtitle: 'Transferencia a cuenta bancaria de EE. UU.',
                description:
                  'Adecuado para transferencias grandes o internacionales.',
                badges: {
                  comingSoon: 'Próximamente',
                  international: 'Internacional',
                  businessDays05: '0-5 días hábiles',
                },
              },
            },
            eur: {
              sepa: {
                title: 'SEPA',
                subtitle: 'Transferencia desde cuenta bancaria de la UE',
                description:
                  'Convierte monedas digitales y recibe EUR en una cuenta bancaria.',
                badges: {
                  recommended: 'Recomendado',
                  instant: 'Instantáneo',
                  noFees: 'Sin comisiones',
                },
              },
              wire: {
                title: 'WIRE',
                subtitle: 'Transferencia a cuenta bancaria de la UE',
                description:
                  'Adecuado para transferencias grandes o internacionales.',
                badges: {
                  international: 'Internacional',
                  businessDays02: '0-2 días',
                  domestic: 'Nacional',
                  businessDays01: '0-1 días hábiles',
                },
              },
              swift: {
                title: 'Swift',
                subtitle: 'Transferencia a cuenta bancaria de EE. UU.',
                description:
                  'Adecuado para transferencias grandes o internacionales.',
                badges: {
                  comingSoon: 'Próximamente',
                  international: 'Internacional',
                  businessDays05: '0-5 días hábiles',
                },
              },
            },
            mxn: {
              spei: {
                title: 'SPEI',
                subtitle: 'Transferencia desde cuenta bancaria MX',
                description:
                  'Convierte monedas digitales y recibe MXN en una cuenta bancaria.',
                badges: {
                  recommended: 'Recomendado',
                  instant: 'Instantáneo',
                  noFees: 'Sin comisiones',
                },
              },
            },
            brl: {
              pix: {
                title: 'PIX',
                subtitle: 'Transferencia desde cuenta bancaria BR',
                description:
                  'Convierte monedas digitales y recibe BRL en una cuenta bancaria.',
                badges: {
                  recommended: 'Recomendado',
                  instant: 'Instantáneo',
                  noFees: 'Sin comisiones',
                },
              },
              wire: {
                title: 'Wire',
                subtitle: 'Transferencia a cuenta bancaria BR',
                description:
                  'Adecuado para transferencias grandes o internacionales.',
                badges: {
                  international: 'Internacional',
                  businessDays02: '0-2 días',
                },
              },
            },
            php: {
              instapay: {
                title: 'Instapay',
                subtitle: 'Transferencia desde cuenta bancaria PH',
                description:
                  'Convierte monedas digitales y recibe PHP en una cuenta bancaria.',
                badges: {
                  amountsSmallerThan50000: 'Montos < 50,000 PHP',
                },
              },
              swiftpay_pesonet: {
                title: 'Swiftpay PesoNet',
                subtitle: 'Transferencia a cuenta bancaria PH',
                description:
                  'Convierte monedas digitales y recibe PHP en una cuenta bancaria.',
                badges: {
                  amountsBiggerOrEqualTo50000: 'Montos ≥ 50,000 PHP',
                },
              },
            },
            inr: {
              neft: {
                title: 'NEFT',
                subtitle: 'Transferencia desde cuenta bancaria IN',
                description:
                  'Convierte monedas digitales y recibe INR en una cuenta bancaria.',
                badges: {
                  recommended: 'Recomendado',
                  instant: 'Instantáneo',
                  noFees: 'Sin comisiones',
                },
              },
              wire: {
                title: 'Wire',
                subtitle: 'Transferencia a cuenta bancaria INR',
                description:
                  'Adecuado para transferencias grandes o internacionales.',
                badges: {
                  international: 'Internacional',
                  businessDays02: '0-2 días',
                },
              },
            },
            gbp: {
              fps: {
                title: 'FPS',
                subtitle: 'Transferencia a cuenta bancaria GBP',
                description:
                  'Convierte monedas digitales y recibe GBP en una cuenta bancaria.',
              },
            },
            cad: {
              eft: {
                title: 'EFT',
                subtitle: 'Transferencia a cuenta bancaria CAD',
                description:
                  'Convierte monedas digitales y recibe CAD en una cuenta bancaria.',
              },
            },
            cop: {
              pse: {
                title: 'PSE',
                subtitle: 'Transferencia a cuenta bancaria COP',
                description:
                  'Convierte monedas digitales y recibe COP en una cuenta bancaria.',
              },
            },
            thb: {
              bank_transfer: {
                title: 'Transferencia Bancaria',
                subtitle: 'Transferencia a cuenta bancaria THB',
                description:
                  'Convierte monedas digitales y recibe THB en una cuenta bancaria.',
              },
            },
            vnd: {
              bank_transfer: {
                title: 'Transferencia Bancaria',
                subtitle: 'Transferencia a cuenta bancaria VND',
                description:
                  'Convierte monedas digitales y recibe VND en una cuenta bancaria.',
              },
            },
            sgd: {
              fast: {
                title: 'FAST',
                subtitle: 'Transferencia a cuenta bancaria SGD',
                description:
                  'Convierte monedas digitales y recibe SGD en una cuenta bancaria.',
              },
            },
            idr: {
              bank_transfer: {
                title: 'Transferencia Bancaria',
                subtitle: 'Transferencia a cuenta bancaria IDR',
                description:
                  'Convierte monedas digitales y recibe IDR en una cuenta bancaria.',
              },
            },
          },
        },
        blocked: {
          title: 'Off-ramping bloqueado',
          description:
            'No tienes acceso para off-ramp ninguna moneda. Por favor, contacta al soporte si necesitas ayuda.',
          cta: 'Contactar Soporte',
        },
      },
      sepaRegionRestriction:
        'Debido a restricciones SEPA relacionadas con MICA, el soporte SEPA para tu región ha sido temporalmente pausado.',
      selectors: {
        wallet: {
          title: 'Seleccionar Billetera',
          drawerTitle: 'Billeteras',
          network: 'Seleccionar Red da Billetera',
          confirm: 'Confirmar Billetera',
          walletHistory: 'Historial de Billeteras',
          addressInputPlaceholder: 'Pegar dirección de billetera',
          confirmAddressInputButton: 'Usar dirección',
          multisigSupportBadge: 'Soporte multifirma',
        },
        card: {
          title: 'Seleccionar Tarjeta',
          drawerTitle: 'Tarjetas',
        },
        bankAccount: {
          title: 'Seleccionar Cuenta Bancaria',
          drawerTitle: 'Cuentas Bancarias',
          emptyTitle: 'No hay cuentas bancarias {{currency}}',
        },
      },
      form: {
        youPay: 'Pagas',
        youReceive: 'Recibes',
        inputEditingTooltip:
          'Actualizaremos automáticamente esta cantidad una vez que termines de editar.',
        maxTransactionSize: 'Tamaño máximo de transacción',
        amountAdjustments: 'Desglose de Tarifas',
        additionalDetails: {
          title: 'Detalles adicionales',
          wireMessage: {
            checkbox: 'Añadir un mensaje de transferencia',
            textareaLabel: 'Descripción',
            info: 'Este mensaje será visible para el destinatario de la transferencia. Máximo de caracteres: 105',
          },
          ffc: {
            checkbox: 'Para crédito adicional (FFC)',
            textareaLabel: 'Referencia del wire',
            textareaPlaceholder: 'Nombre, 0000-0000',
          },
        },
        cta: {
          selectAWalletFirst: 'Selecciona una billetera primero',
          selectACardFirst: 'Selecciona una tarjeta primero',
          selectABankAccountFirst: 'Selecciona una cuenta bancaria primero',
          create: 'Crear transferencia',
          getInstructions: 'Obtener instrucciones',
        },
        walletBalance: {
          label: 'Saldo de la billetera:',
          actions: {
            max: 'Máximo',
            half: 'Medio',
          },
        },
        errors: {
          walletNotSupported: {
            title: 'Billetera no soportada',
            description:
              'La billetera seleccionada no es compatible con esta transferencia.',
          },
          verifyCurrencyPair: 'Par de divisas no admitido en esta red',
          verifyAmounts: {
            source: {
              min: 'El importe de la fuente es inferior al mínimo',
              max: 'La cantidad de origen es mayor que el máximo',
            },
            destination: {
              min: 'El importe del destino es inferior al mínimo',
              max: 'El importe del destino es mayor que el máximo',
            },
          },
        },
      },

      onRampInstructions: {
        title: 'Instrucciones de transferencia',
        consent: {
          title: 'Por favor, confirma',
          description:
            'Yo he transferido la cantidad correcta desde mi cuenta bancaria a la cuenta indicada vía {{transferMethod}} usando los datos proporcionados arriba.',
        },
        depositMessageAlert: {
          title: 'Asegúrate de incluir este mensaje de depósito',
          description: 'Sin él, tu transferencia puede ser rechazada.',
        },
        transferMethodAlert: {
          title: 'Solo transferencia {{transferMethod}}',
          description:
            'Por favor, no envíe fondos a través de {{alternativeTransferMethod1}} o {{alternativeTransferMethod2}}. Debe realizarse una transferencia {{transferMethod}}. Asegúrese de que todos los datos coincidan exactamente.',
        },
      },
      tooltips: {
        sepa: 'Para acceder a los pagos SEPA, debe completar el proceso de KYC y proporcionar una prueba de dirección. Si completó el KYC sin enviar la prueba de dirección, comuníquese con su comerciante para continuar.',
        isAboveLimit:
          'El monto ingresado excede el tamaño máximo de transacción de ${maxSingleTxSizeFormatted}',
        currencies: 'Esta moneda no está disponible para su cuenta',
      },
    },
    myPayouts: {
      title: 'Historial de Transferencias',
      table: {
        header: {
          type: 'Tipo',
          source: 'Origen',
          destination: 'Destino',
          amountSent: 'Enviado',
          amountReceived: 'Recibido',
          status: 'Estado',
          actions: 'Acciones',
          date: 'Fecha',
        },
      },
      searchPlaceholder: 'Buscar...',
      onRamps: {
        tabTitle: 'On-ramp',
        empty: {
          title: 'No hay tranferencias de on-ramp',
          description:
            'Con {{merchantName}} puedes entrar y salir de intercambios, billeteras y bancos de manera fluida. Convierte tu moneda digital en moneda fiat y viceversa.',
          action: 'Crear tranferencia',
        },
      },
      offRamps: {
        tabTitle: 'Off-ramp',
        empty: {
          title: 'No hay tranferencias de off-ramp',
          description:
            'Con {{merchantName}} puedes entrar y salir de intercambios, billeteras y bancos de manera fluida. Convierte tu moneda digital en moneda fiat y viceversa.',
          action: 'Crear tranferencia',
        },
      },
      history: {
        tabTitle: 'Historial',
        empty: {
          title: 'No hay tranferencias',
          description:
            'Con {{merchantName}} puedes entrar y salir de intercambios, billeteras y bancos de manera fluida. Convierte tu moneda digital en moneda fiat y viceversa.',
          action: 'Crear tranferencia',
        },
      },
      detail: {
        id: 'ID',
        type: {
          onRamp: 'On-ramp',
          offRamp: 'Off-ramp',
          onChain: 'On-chain',
        },
        fiatNetwork: {
          wire: 'Transferencia bancaria',
          ach: 'Transferencia ACH',
          sepa: 'Transferencia SEPA',
        },
        info: {
          created: 'Creado',
          paymentMethod: 'Método de pago',
          email: 'Correo electrónico',
        },
        payment: {
          title: 'Detalles de pago',
          amount: 'Monto',
          type: 'Tipo',
          from: 'Desde',
          to: 'Hacia',
          summary: 'Resumen',
          fees: 'Tarifas',
        },
        bank: {
          title: 'Detalles bancarios',
          name: 'Nombre',
          accountType: 'Tipo de cuenta',
          bankName: 'Nombre del banco',
          beneficiaryName: 'Nombre del beneficiario',
          bankAddress: 'Dirección del banco',
          beneficiaryAddress: 'Dirección del beneficiario',
          accountNumber: 'Número de cuenta',
          routingNumber: 'Número de ruta',
        },
        instructions: {
          title: 'Instrucciones de transferencia',
          amountToSend: 'Monto a enviar',
          message: {
            ach: 'Mensaje de ACH',
            wire: 'Mensaje de transferencia',
          },
        },
        cancelCta: 'Cancelar transferencia',
        cancellationWarning: {
          title: 'Verifica antes de confirmar',
          description:
            'Confirma solo si no has transferido ningún {{currency}} a la dirección <0>{{address}}</0> en la blockchain de {{blockchain}}. De lo contrario, puede resultar en la pérdida de fondos.',
          cta: 'Confirmar cancelación',
        },
        onRampReminder: {
          title: 'El proceso de on-ramp en Sphere es manual.',
          description:
            'Si aún no lo has hecho, <0>envía una transferencia {{transferMethod}} desde tu cuenta a Sphere con los datos abajo</0>. Le enviaremos un correo electrónico cuando su transferencia esté completa.',
        },
      },
    },
    tos: {
      alreadyAcceptedCtaText: 'Volver',
      successCtaText: 'Continuar',
    },
    languageSelector: {
      label: 'Cambiar idioma',
    },
    tables: {
      showing: 'Mostrando {{from}}-{{to}} de {{total}} resultados',
      previous: 'Anterior',
      next: 'Siguiente',
    },
    badges: {
      bankAccount: {
        pending: {
          text: 'Pendiente',
          description:
            'La cuenta bancaria está siendo verificada, este proceso típicamente toma de algunos segundos a unos pocos minutos. Las cuentas bancarias pendientes no pueden ser utilizadas para transferencias.',
        },
        active: {
          text: 'Activa',
          description:
            'La cuenta bancaria está activa y lista para ser utilizada para transferencias.',
        },
        inactive: {
          text: 'Eliminada',
          description: 'La cuenta bancaria fue eliminada.',
        },
        invalid: {
          text: 'Inválida',
          description: 'La cuenta bancaria es inválida.',
        },
      },
      payout: {
        pending: {
          text: 'Pendiente',
          description: 'La transferencia está pendiente de aprobación.',
        },
        funded: {
          text: 'Fondeado',
          description: 'La transferencia ha sido financiado.',
        },
        refunded: {
          text: 'Reembolsado',
          description: 'La transferencia fue reembolsado.',
        },
        processing: {
          text: 'Procesando',
          description: 'La transferencia está siendo procesado actualmente.',
        },
        succeeded: {
          text: 'Exitoso',
          description: 'La transferencia se completó con éxito.',
        },
        failed: {
          text: 'Fallido',
          description: 'La transferencia falló.',
        },
        failedPrecondition: {
          text: 'Fallido',
          description: 'La transferencia falló.',
        },
        cancelled: {
          text: 'Cancelado',
          description: 'La transferencia fue cancelado.',
        },
      },
      kyc: {
        approved: {
          text: 'Verificado',
        },
        rejected: {
          text: 'Rechazado',
        },
        incomplete: {
          text: 'Incompleto',
        },
        pending: {
          text: 'Pendiente',
        },
        additionalReviewRequired: {
          text: 'Revisión adicional requerida',
        },
      },
      sepa: {
        approved: {
          text: 'Activo',
        },
        incomplete: {
          text: 'Inactivo',
        },
      },
    },
    notifications: {
      otpEmailResent: {
        title: 'Correo enviado',
        description: 'Revisa tu correo para el código de verificación',
      },
      otpEmailResentError: {
        title: 'Algo salió mal',
        description:
          'No pudimos reenviar el código de verificación en este momento. Por favor, inténtalo de nuevo más tarde.',
      },
      signInSuccess: {
        title: 'Correo verificado',
        description: 'Ahora estás conectado a Ramp',
      },
      kycDone: {
        title: 'Verificación de identidad completada',
        description:
          'Tu verificación de identidad ha sido completada/está en revisión.',
      },
      bankAccountDeleteSuccess: {
        title: 'Cuenta bancaria eliminada',
        description: 'Tu cuenta bancaria ha sido eliminada.',
      },
      bankAccountDeleteError: {
        title: 'Algo salió mal',
        description:
          'No pudimos eliminar tu cuenta bancaria en este momento. ({{error}})',
      },
      payouts: {
        create: {
          success: {
            title: 'Transferencia iniciada',
            onRampDescription: 'Sua transfêrncia de {{type}} foi iniciada.',
            offRampDescription: 'Sua transfêrncia de {{type}} foi iniciada.',
          },
          error: {
            title: 'Algo salió mal',
            description:
              'No pudimos crear tu transferencia de {{type}} en este momento. ({{error}})',
          },
        },
      },
    },
    settings: {
      title: 'Configuración',
      loginEmail: {
        label: 'Correo electrónico',
        description:
          'Correo electrónico asociado a tu cuenta RAMP. No se puede modificar.',
      },
      kyc: {
        title: 'Mi KYC/B',
        cta: 'Reenviar',
      },
      sepa: {
        title: 'Transferencias SEPA',
        description: {
          incomplete:
            'Esta cuenta fue verificada antes de la adición de transferencias SEPA, las cuales requieren prueba de domicilio para su acceso. Por favor, reenvía tus documentos KYC/B para habilitar SEPA.',
        },
        cta: 'Reenviar',
      },
      customerId: 'ID del cliente',
      form: {
        name: 'Nombre',
        phone: 'Número de teléfono',
        cta: 'Guardar cambios',
      },
    },
    translations: {
      amountAdjustmentType: {
        networkFee: 'Tarifa de red',
        sphereConversionFee: 'Tarifa de aplicación de conversión',
        sphereFixedFee: 'Tarifa de aplicación fija',
        merchantConversionFee: 'Tarifa de comerciante de conversión',
        merchantFixedFee: 'Tarifa de comerciante fija',
        conversionFee: 'Tarifa de conversión',
        fxRate: 'Tasa de cambio',
        noFxRateMessage: '',
        sponsoredFee: 'Monto de patrocinio',
        totalFee: 'Total',
        pay: 'Tú pagas',
        receive: 'Tú recibes',
      },
      fees: 'Tarifas',
    },
    apiErrors: {
      'application/invalid-id': 'ID de Aplicación inválido.',
      'application/not-found': 'Aplicación no encontrada.',
      'ramp/auth/bad-host': 'Ramp no está configurado para este comerciante.',
      'ramp/auth/host-access': 'Los comerciantes no pueden autenticarse.',
      'wallets/duplicate': 'La billetera ya existe.',
      'wallets/invalid-address': 'La dirección de billetera no es válida.',
      'wallets/not-found': 'Billetera no encontrada.',
      'wallets/unsafe-address': 'No se puede interactuar con esta billetera.',
      'wallets/unsupported-network': 'Red no compatible.',
      unknown: 'Algo salió mal.',
    },
    apply: 'Aplicar',
    continue: 'Continuar',
    back: 'Atrás',
    backToSphere: 'Volver a Ramp',
    cancel: 'Cancelar',
    comingSoon: 'Próximamente',
    collapse: 'Colapsar',
    expand: 'Expandir',
    export: 'Exportar',
    filter: 'Filtrar',
    copied: '¡Copiado!',
    selected: 'Seleccionado',
    poweredBy: 'potenciado por',
    signOut: 'Cerrar sesión',
    deleted: 'Eliminada',
    type: 'Tipo',
    network: 'Red',
    status: 'Estado',
    reset: 'Restablecer',
    msa: {
      title: 'Enviar MSA',
      pendingSignature: 'Firma Pendiente',
      pendingMessage:
        'Hemos enviado un enlace de verificación de MSA de DocuSign a {{email}}. Una vez que firme el documento, su estado se actualizará y podrá continuar con el siguiente paso del proceso de incorporación.',
      statusUpdateNote:
        'Puede tomar hasta 15 minutos para que su estado se actualice después de firmar',
      cancel: 'Atrás',
      resendEmail: 'Reenviar correo',
      contactPerson: {
        heading: 'Persona de Contacto',
        title: 'Título',
        titlePlaceholder: '(ej. Director)',
        firstName: 'Nombre',
        firstNamePlaceholder: '(ej. John)',
        lastName: 'Apellido',
        lastNamePlaceholder: '(ej. Smith)',
        email: 'Correo electrónico',
        emailPlaceholder: '(ej. john.smith@email.com)',
      },
      company: {
        title: 'Empresa',
        name: 'Nombre de la empresa',
        namePlaceholder: '(ej. Acme Inc.)',
        addressLine1: 'Dirección línea 1',
        addressLine1Placeholder: '(ej. 123 Main St)',
        addressLine2: 'Dirección línea 2',
        addressLine2Placeholder: '(ej. Unit 102)',
        city: 'Ciudad',
        cityPlaceholder: '(ej. New York)',
        state: 'Estado',
        statePlaceholder: 'Seleccionar estado',
        country: 'País',
        countryPlaceholder: 'Seleccionar país',
        postalCode: 'Código postal',
        postalCodePlaceholder: '(ej. 10001)',
      },
      authorizedSignerNote:
        'Por favor proporcione el nombre y correo electrónico de una persona autorizada para firmar en nombre de su empresa.',
      sendEmail: 'Enviar correo',
      success: {
        title: 'Éxito',
        description: 'Por favor firme el acuerdo enviado a {{email}}',
      },
      error: {
        title: 'Error',
        description: 'Ocurrió un error, {{error}}',
      },
    },
  },
} as const satisfies LocaleResource;

export default es;
